import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

// Register service worker for PWA
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js")
      .then(() => console.log("SW registered"))
      .catch((err) => console.log("SW registration failed:", err));
  });
}

const SUPABASE_URL = "https://xsscbbqnqjelioebbjgk.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inhzc2NiYnFucWplbGlvZWJiamdrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQyMDgxNzQsImV4cCI6MjA4OTc4NDE3NH0.p7gMiTEX8Lf3VPEj2t0Zj2Jm4tL0RvttLEeUeU8J_u4";

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    detectSessionInUrl: true,
    storageKey: "ryp_auth",
    storage: window.localStorage,
  },
});

const VISIT_FIELDS = "id, rest, date, address, place_id, dining_type, venue_type, cuisine, user_id, created_at";
const DISH_FIELDS = "id, visit_id, name, rating, cat, comment, beer_type, abv, photo, user_id";
const WAITLIST_FIELDS = "id, rest, address, place_id, venue_type, note, user_id, created_at";
const PROFILE_FIELDS = "id, email, username, avatar_url";
const CATS = ["Appetizer", "Main", "Side", "Dessert", "Special"];
const CUISINES = ["American", "Italian", "Mexican", "Japanese", "Chinese", "Thai", "Indian", "Mediterranean", "French", "Greek", "Korean", "Vietnamese", "Spanish", "Middle Eastern", "Caribbean", "BBQ", "Seafood", "Pizza", "Burgers", "Steakhouse", "Vegan", "Other"];
const LABELS = { 0: "", 1: "Poor", 2: "Fair", 3: "Good", 4: "Great", 5: "Amazing!" };

// ── helpers ──────────────────────────────────────────────
function avgRating(dishes) {
  const rated = dishes.filter((d) => d.rating > 0);
  if (!rated.length) return 0;
  return rated.reduce((s, d) => s + d.rating, 0) / rated.length;
}

function getDisplayName(profile) {
  if (!profile) return "Friend";
  return profile.username || profile.email?.split("@")[0] || "Friend";
}

function getFriendIds(friendships, userId) {
  return (friendships || [])
    .filter(f => f.status === "accepted")
    .map(f => f.requester_id === userId ? f.addressee_id : f.requester_id);
}

// ── photo helpers ─────────────────────────────────────────
async function compressImage(file, maxWidth = 800, quality = 0.75) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
    };
    img.src = url;
  });
}

async function uploadPhoto(file, userId) {
  try {
    const compressed = await compressImage(file);
    const ext = "jpg";
    const path = `${userId}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("dish-photos").upload(path, compressed, { contentType: "image/jpeg" });
    if (error) throw error;
    const { data } = supabase.storage.from("dish-photos").getPublicUrl(path);
    return data.publicUrl;
  } catch (err) {
    console.error("Photo upload failed:", err);
    return null;
  }
}

// Convert any existing base64 photo to a display-ready src
function photoSrc(photo) {
  if (!photo) return null;
  // Already a URL (from storage)
  if (photo.startsWith("http")) return photo;
  // Legacy base64
  return photo;
}

// ── CHEF'S KISS ───────────────────────────────────────────
function ChefKissIcon({ size = 24, active = false }) {
  // Clean recognizable chef's kiss — fingers pinched to lips
  const c = active ? "#C94F2C" : "#999";
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Chef hat brim */}
      <rect x="8" y="13" width="16" height="3" rx="1.5" fill={c}/>
      {/* Chef hat top */}
      <ellipse cx="16" cy="10" rx="7" ry="5" fill={c}/>
      {/* Face */}
      <circle cx="16" cy="19" r="6" fill={active ? "#FAECE7" : "#f0f0f0"} stroke={c} strokeWidth="1.5"/>
      {/* Eyes */}
      <circle cx="13.5" cy="18" r="1" fill={c}/>
      <circle cx="18.5" cy="18" r="1" fill={c}/>
      {/* Kiss lips — puckered */}
      <ellipse cx="16" cy="21.5" rx="2.5" ry="1.5" fill={active ? "#C94F2C" : "#bbb"}/>
      <ellipse cx="16" cy="21" rx="2" ry="0.8" fill={active ? "#e8745a" : "#ccc"}/>
      {/* Sparkle when active */}
      {active && <text x="23" y="13" fontSize="6" fill="#C94F2C">✨</text>}
    </svg>
  );
}

function ChefKissButton({ targetType, targetId, user, size = 22 }) {
  const [kissed, setKissed] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!targetId) return;
    const id = String(targetId);
    supabase.from("chefs_kisses")
      .select("id, user_id")
      .eq("target_type", targetType)
      .eq("target_id", id)
      .then(({ data }) => {
        setCount((data || []).length);
        setKissed((data || []).some(k => k.user_id === user?.id));
      });
  }, [targetId]);

  async function toggle(e) {
    e.stopPropagation();
    if (!user || loading) return;
    setLoading(true);
    const id = String(targetId);
    try {
      if (kissed) {
        const { error } = await supabase.from("chefs_kisses").delete()
          .eq("user_id", user.id)
          .eq("target_type", targetType)
          .eq("target_id", id);
        if (error) throw error;
        setKissed(false);
        setCount(c => Math.max(0, c - 1));
      } else {
        const { error } = await supabase.from("chefs_kisses").insert({
          user_id: user.id,
          target_type: targetType,
          target_id: id,
        });
        if (error) { alert("Kiss save error: " + error.message); throw error; }
        setKissed(true);
        setCount(c => c + 1);
      }
    } catch (err) { console.error("Chef kiss error:", err); }
    finally { setLoading(false); }
  }

  return (
    <button onClick={toggle} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: kissed ? "#FFF5F2" : "var(--color-background-secondary)", border: kissed ? "1.5px solid #C94F2C" : "0.5px solid var(--color-border-secondary)", borderRadius: 20, padding: "4px 10px 4px 6px", cursor: "pointer", opacity: loading ? 0.6 : 1, transition: "all 0.15s" }}>
      <ChefKissIcon size={size} active={kissed} />
      {count > 0 && <span style={{ fontSize: 12, fontWeight: 600, color: kissed ? "#C94F2C" : "var(--color-text-secondary)" }}>{count}</span>}
    </button>
  );
}

function StarDisplay({ rating, size = 16 }) {
  return (
    <span style={{ color: "#C94F2C", fontSize: size }}>
      {"★".repeat(rating)}
      <span style={{ color: "#D3D1C7" }}>{"★".repeat(5 - rating)}</span>
    </span>
  );
}

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {[1, 2, 3, 4, 5].map((v) => (
        <span
          key={v}
          onClick={() => onChange(v)}
          onMouseEnter={() => setHover(v)}
          onMouseLeave={() => setHover(0)}
          style={{
            fontSize: 36,
            cursor: "pointer",
            color: (hover || value) >= v ? "#C94F2C" : "#D3D1C7",
            lineHeight: 1,
            userSelect: "none",
            transition: "color 0.08s, transform 0.08s",
            transform: hover === v ? "scale(1.2)" : "scale(1)",
          }}
        >
          ★
        </span>
      ))}
      {(hover || value) > 0 && (
        <span style={{ fontSize: 13, fontWeight: 500, color: "#C94F2C", marginLeft: 6 }}>
          {LABELS[hover || value]}
        </span>
      )}
    </div>
  );
}

// ── AVATAR ────────────────────────────────────────────────
// size: px number. url: avatar_url from profile. fallback: letter or emoji.
function Avatar({ url, fallback = "👤", size = 36, border = false }) {
  const s = {
    width: size, height: size, borderRadius: "50%", flexShrink: 0,
    border: border ? "2.5px solid #C94F2C" : "none",
    objectFit: "cover", display: "flex", alignItems: "center", justifyContent: "center",
  };
  if (url) {
    return <img src={url} alt="avatar" style={{ ...s, objectFit: "cover" }} />;
  }
  const isEmoji = typeof fallback === "string" && /\p{Emoji}/u.test(fallback);
  return (
    <div style={{ ...s, background: "#FAECE7", fontSize: isEmoji ? size * 0.5 : size * 0.4, fontWeight: 600, color: "#C94F2C" }}>
      {fallback}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
      <img src="/favicon.ico" alt="" style={{ width: 32, height: 32, animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── SHARED TOPBAR ─────────────────────────────────────────
function TopBar({ subtitle, left, right }) {
  return (
    <div style={{ flexShrink: 0 }}>
      <div style={styles.topbar}>
        {left && (
          <div style={{ marginBottom: 4 }}>{left}</div>
        )}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={styles.logo}>Rate Your Plate</div>
            {subtitle && (
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {subtitle}
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {right && <div style={{ flexShrink: 0 }}>{right}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
function RestaurantSearch({ onSelect }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualAddress, setManualAddress] = useState("");

  useEffect(() => {
    if (!inputRef.current) return;
    let cancelled = false;

    function init() {
      if (cancelled) return;
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        setTimeout(init, 200);
        return;
      }
      if (autocompleteRef.current) return; // already initialised
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ["establishment"],
        fields: ["name", "formatted_address", "place_id"],
      });
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (!place.name) return;
        const result = {
          name: place.name,
          address: place.formatted_address || "",
          place_id: place.place_id || null,
          source: "google",
        };
        setSelected(result);
        onSelect(result);
      });
    }

    init();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  function handleManualSave() {
    if (!manualName.trim()) return;
    const place = {
      name: manualName.trim(),
      address: manualAddress.trim(),
      place_id: null,
      source: "manual",
    };
    setSelected(place);
    setShowManual(false);
    onSelect(place);
  }

  if (selected) {
    return (
      <div style={styles.selectedRestaurant}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
            {selected.name}
          </div>
          {selected.address && (
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
              {selected.address}
            </div>
          )}
        </div>
        <button
          style={styles.changeBtn}
          onClick={() => {
            setSelected(null);
            onSelect(null);
            setShowManual(false);
            autocompleteRef.current = null;
            // re-init on next render
            setTimeout(() => {
              if (inputRef.current && window.google) {
                autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
                  types: ["establishment"],
                  fields: ["name", "formatted_address", "place_id"],
                });
                autocompleteRef.current.addListener("place_changed", () => {
                  const place = autocompleteRef.current.getPlace();
                  if (!place.name) return;
                  const result = {
                    name: place.name,
                    address: place.formatted_address || "",
                    place_id: place.place_id || null,
                    source: "google",
                  };
                  setSelected(result);
                  onSelect(result);
                });
              }
            }, 100);
          }}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        style={styles.input}
        placeholder="Search for a restaurant..."
        autoComplete="off"
      />
      <div style={{ marginTop: 8 }}>
        <span
          style={{ fontSize: 12, color: "#C94F2C", cursor: "pointer", fontWeight: 500 }}
          onClick={() => setShowManual(!showManual)}
        >
          + Can't find it? Add manually
        </span>
      </div>

      {showManual && (
        <div style={styles.manualBox}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 10 }}>
            Add restaurant manually
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={styles.dbLabel}>Name</div>
            <input
              style={styles.input}
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder="Restaurant name"
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={styles.dbLabel}>Address</div>
            <input
              style={styles.input}
              value={manualAddress}
              onChange={(e) => setManualAddress(e.target.value)}
              placeholder="123 Main St, City, State"
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{ ...styles.addDishBtn, marginTop: 0 }} onClick={handleManualSave}>Save</button>
            <button
              style={{ ...styles.addDishBtn, marginTop: 0, background: "var(--color-background-secondary)", color: "var(--color-text-secondary)", border: "0.5px solid var(--color-border-secondary)" }}
              onClick={() => setShowManual(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── HISTORY SCREEN ───────────────────────────────────────
function WishlistDetailScreen({ item, onSave, onDelete, onClose, onNavigate, friendRequestCount }) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(item.note || "");
  const [venueType, setVenueType] = useState(item.venue_type || "restaurant");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    await onSave(item.id, { note: note.trim() || null, venue_type: venueType });
    setSaving(false);
    setEditing(false);
  }

  const ds = new Date(item.created_at).toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
  const vEmoji = VENUE_TYPES.find(v => v.id === (editing ? venueType : item.venue_type))?.emoji || "";
  const vLabel = VENUE_TYPES.find(v => v.id === (editing ? venueType : item.venue_type))?.label || "";

  return (
    <div style={styles.screen}>
      <TopBar
        subtitle={item.rest}
        left={<button style={styles.backBtn} onClick={onClose}>‹ Back</button>}
        right={
          <div style={{ display: "flex", gap: 8 }}>
            <button style={styles.actionBtnBlue} onClick={() => setEditing(!editing)}>{editing ? "Cancel" : "Edit"}</button>
            <button style={styles.actionBtnRed} onClick={() => { if (window.confirm("Remove from Wishlist?")) onDelete(item.id); }}>Delete</button>
          </div>
        }
      />
      <div style={styles.scrollBody}>
        <div style={styles.detailHeader}>
          <div style={styles.detailRest}>{item.rest}</div>
          {item.address && <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 6 }}>{item.address}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>{ds}</span>
            <span style={{ fontSize: 12, background: "#EEF2FF", color: "#4338CA", borderRadius: 20, padding: "2px 10px", fontWeight: 500 }}>📋 Wishlist</span>
            {vEmoji && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{vEmoji} {vLabel}</span>}
          </div>
        </div>

        {!editing && item.note && (
          <div style={{ ...styles.detailDish }}>
            <div style={styles.catGroupLabel}><span style={styles.catDot} />Note</div>
            <div style={{ fontSize: 14, color: "var(--color-text-primary)", lineHeight: 1.6 }}>{item.note}</div>
          </div>
        )}

        {editing && (
          <>
            <div style={styles.formSection}>
              <div style={styles.formLabel}>Venue type</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "nowrap", overflowX: "auto" }}>
                {VENUE_TYPES.map(v => (
                  <span key={v.id} onClick={() => setVenueType(v.id)} style={{
                    padding: "8px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500,
                    cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", flexShrink: 0,
                    border: venueType === v.id ? "none" : "0.5px solid var(--color-border-secondary)",
                    background: venueType === v.id ? "#C94F2C" : "var(--color-background-secondary)",
                    color: venueType === v.id ? "#fff" : "var(--color-text-secondary)",
                  }}>{v.emoji} {v.label}</span>
                ))}
              </div>
            </div>
            <div style={styles.formSection}>
              <div style={styles.formLabel}>Note (optional)</div>
              <input style={styles.input} value={note} onChange={e => setNote(e.target.value)}
                placeholder="e.g. Try the pasta, go on weekends" />
            </div>
            <button style={{ ...styles.saveVisitBtn, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        )}

        <div style={{ height: 16 }} />
      </div>
      <BottomNav active="venues" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}

function VenuesScreen({ visits, loading, user, onNewVisit, onOpenVisit, onOpenRestaurant, onNavigate, friendRequestCount, onRefresh, cachedFriendships }) {
  const [query, setQuery] = useState("");
  const [showMine, setShowMine] = useState(true);
  const [showFriends, setShowFriends] = useState(true);
  const [friendVisits, setFriendVisits] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [friendWishlist, setFriendWishlist] = useState([]);
  const [editingWishlist, setEditingWishlist] = useState(null);
  const [myProfile, setMyProfile] = useState({ avatar_url: null, username: null });

  // Pull to refresh
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(null);
  const scrollRef = useRef(null);

  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    await Promise.all([
      onRefresh?.(),
      loadFriendVisits(),
      loadWishlist(),
    ]);
    setRefreshing(false);
  }

  function onTouchStart(e) {
    if (scrollRef.current?.scrollTop === 0) touchStartY.current = e.touches[0].clientY;
  }
  function onTouchEnd(e) {
    if (touchStartY.current === null) return;
    const diff = e.changedTouches[0].clientY - touchStartY.current;
    if (diff > 80) handleRefresh();
    touchStartY.current = null;
  } // the item being edited
  const [showPanel, setShowPanel] = useState(false);
  const [filterOpenSection, setFilterOpenSection] = useState(null); // "show"|"venue"|"rating"|"cuisine"
  const [minStars, setMinStars] = useState(0);
  const [typeFilter, setTypeFilter] = useState("all"); // "visited" | "wishlist" | "all"
  const [venueTypeFilter, setVenueTypeFilter] = useState("");
  const [cuisineFilter, setCuisineFilter] = useState("");

  useEffect(() => {
    loadFriendVisits();
    loadWishlist();
    supabase.from("profiles").select("username, avatar_url").eq("id", user.id).single()
      .then(({ data }) => { if (data) setMyProfile(data); });

    // Real-time subscription — refresh when any visit changes
    const channel = supabase.channel("venues-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "visits" }, () => {
        onRefresh?.();
        loadFriendVisits();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "waitlist" }, () => {
        loadWishlist();
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  async function loadWishlist() {
    try {
      const { data } = await supabase.from("waitlist").select(WAITLIST_FIELDS).eq("user_id", user.id).order("created_at", { ascending: false });
      setWishlist((data || []).map(w => ({ ...w, isWishlist: true, dishes: [], rest: w.rest })));
    } catch (err) { console.error(err); }
  }

  async function deleteWishlistItem(id) {
    try {
      await supabase.from("waitlist").delete().eq("id", id);
      setWishlist(prev => prev.filter(w => w.id !== id));
      setEditingWishlist(null);
    } catch (err) { alert("Error deleting: " + err.message); }
  }

  async function saveWishlistItem(id, updates) {
    try {
      await supabase.from("waitlist").update(updates).eq("id", id);
      setWishlist(prev => prev.map(w => w.id === id ? { ...w, ...updates } : w));
      setEditingWishlist(null);
    } catch (err) { alert("Error saving: " + err.message); }
  }

  async function loadFriendVisits() {
    setFriendsLoading(true);
    try {
      // Use cached friendships from App if available, otherwise fetch
      let allFriendships = cachedFriendships?.length ? cachedFriendships : null;
      if (!allFriendships) {
        const { data } = await supabase.from("friendships").select("id, requester_id, addressee_id, status");
        allFriendships = data || [];
      }
      const friendIds = getFriendIds(allFriendships, user.id);
      if (!friendIds.length) { setFriendVisits([]); setFriendWishlist([]); setFriendsLoading(false); return; }
      const { data: profiles } = await supabase.from("profiles").select("id, email, username, avatar_url").in("id", friendIds);
      const profileMap = {};
      (profiles || []).forEach(p => profileMap[p.id] = p);

      // Fetch friend visits
      const { data: fVisits } = await supabase.from("visits").select(VISIT_FIELDS).in("user_id", friendIds).order("date", { ascending: false });
      const { data: fDishes } = await supabase.from("dishes")
        .select("id, visit_id, name, rating, cat, comment, beer_type, abv")
        .in("visit_id", (fVisits || []).map(v => v.id));
      setFriendVisits((fVisits || []).map(v => ({
        ...v,
        dishes: (fDishes || []).filter(d => d.visit_id === v.id),
        friendName: getDisplayName(profileMap[v.user_id]), friendAvatar: profileMap[v.user_id]?.avatar_url || null,
        isFriend: true,
      })));

      // Fetch friend wishlists
      const { data: fWishlist } = await supabase.from("waitlist").select(WAITLIST_FIELDS).in("user_id", friendIds);
      setFriendWishlist((fWishlist || []).map(w => ({
        ...w,
        isWishlist: true,
        isFriend: true,
        dishes: [],
        rest: w.rest,
        friendName: getDisplayName(profileMap[w.user_id]), friendAvatar: profileMap[w.user_id]?.avatar_url || null,
      })));
    } catch (err) { console.error(err); }
    finally { setFriendsLoading(false); }
  }

  const isSearching = query.trim().length > 0;
  const allVisits = [...(showMine ? visits : []), ...(showFriends ? friendVisits : [])];

  // Include my wishlist + friend wishlists in search and feed
  const allWishlist = [
    ...(showMine ? wishlist : []),
    ...(showFriends ? friendWishlist : []),
  ];
  const allVisitsWithWishlist = [...allVisits, ...allWishlist];

  // Search results — venue cards grouped by place
  const searchResults = (() => {
    if (!isSearching) return [];
    const restMap = {};
    allVisitsWithWishlist.forEach(v => {
      const key = v.place_id || v.rest;
      if (!restMap[key]) restMap[key] = { key, name: v.rest, address: v.address || "", place_id: v.place_id, visits: [], allDishes: [], hasFriendVisits: false, hasWishlist: false, venue_type: v.venue_type };
      restMap[key].visits.push(v);
      if (!v.isWishlist) restMap[key].allDishes.push(...(v.dishes || []));
      if (v.isFriend) restMap[key].hasFriendVisits = true;
      if (v.isWishlist) restMap[key].hasWishlist = true;
    });
    const q = query.toLowerCase();
    return Object.values(restMap)
      .filter(r => r.name.toLowerCase().includes(q) || r.address.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  })();

  // Merge visits + wishlist into one chronological feed
  const showWishlistItems = !isSearching && (typeFilter === "all" || typeFilter === "wishlist");
  const showVisitsInFeed = typeFilter === "all" || typeFilter === "visited";
  const filteredWishlist = allWishlist.filter(w => !venueTypeFilter || w.venue_type === venueTypeFilter);
  const hasActiveFilter = minStars > 0 || typeFilter !== "all" || venueTypeFilter !== "" || cuisineFilter !== "";

  const feedItems = isSearching ? [] : [
    ...(showVisitsInFeed ? allVisits
      .filter(v => minStars === 0 || avgRating(v.dishes) >= minStars)
      .filter(v => !venueTypeFilter || v.venue_type === venueTypeFilter)
      .filter(v => !cuisineFilter || v.cuisine === cuisineFilter) : []),
    ...(showWishlistItems ? filteredWishlist : []),
  ].sort((a, b) => {
    const dateA = new Date(a.date || a.created_at);
    const dateB = new Date(b.date || b.created_at);
    return dateB - dateA;
  });

  // Show wishlist detail as full screen replacement
  if (editingWishlist) {
    return (
      <WishlistDetailScreen
        item={editingWishlist}
        onSave={saveWishlistItem}
        onDelete={deleteWishlistItem}
        onClose={() => setEditingWishlist(null)}
        onNavigate={onNavigate}
        friendRequestCount={friendRequestCount}
      />
    );
  }

  return (
    <div style={styles.screen}>
      <TopBar subtitle="Venues" right={
        <button style={styles.btnPrimary} onClick={onNewVisit}>Add a Spot</button>
      } />

      {/* Mine / Everyone filter bar */}
      <div style={{ padding: "8px 16px", background: "var(--color-background-primary)", borderBottom: "0.5px solid var(--color-border-tertiary)", display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ display: "flex", background: "var(--color-background-secondary)", borderRadius: 10, padding: 3, border: "0.5px solid var(--color-border-secondary)", flex: 1 }}>
          <div onClick={() => { setShowMine(true); setShowFriends(false); }} style={{ flex: 1, textAlign: "center", padding: "7px 0", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", background: (showMine && !showFriends) ? "var(--color-background-primary)" : "transparent", color: (showMine && !showFriends) ? "#C94F2C" : "var(--color-text-secondary)", boxShadow: (showMine && !showFriends) ? "0 1px 4px rgba(0,0,0,0.1)" : "none" }}>Mine</div>
          <div onClick={() => { setShowMine(true); setShowFriends(true); }} style={{ flex: 1, textAlign: "center", padding: "7px 0", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", background: (showMine && showFriends) ? "var(--color-background-primary)" : "transparent", color: (showMine && showFriends) ? "#C94F2C" : "var(--color-text-secondary)", boxShadow: (showMine && showFriends) ? "0 1px 4px rgba(0,0,0,0.1)" : "none" }}>Everyone</div>
        </div>
        <div style={{ position: "relative", cursor: "pointer" }} onClick={() => setShowPanel(!showPanel)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: minStars > 0 ? "#C94F2C" : "var(--color-text-secondary)" }}>
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="20" y2="12"/><line x1="12" y1="18" x2="20" y2="18"/>
          </svg>
          {minStars > 0 || typeFilter !== "all" || venueTypeFilter ? <div style={{ position: "absolute", top: -3, right: -3, width: 8, height: 8, borderRadius: "50%", background: "#C94F2C", border: "2px solid var(--color-background-primary)" }} /> : null}
        </div>
      </div>

      {showPanel && (
        <div style={{ borderBottom: "0.5px solid var(--color-border-tertiary)", background: "var(--color-background-primary)" }}>

          {/* Show */}
          <div style={{ borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
            <div onClick={() => setFilterOpenSection(filterOpenSection === "show" ? null : "show")}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 20px", cursor: "pointer", background: "var(--color-background-secondary)" }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>Show</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {typeFilter !== "all" && <span style={{ fontSize: 12, color: "#C94F2C", fontWeight: 500 }}>{typeFilter === "visited" ? "✓ Visited" : "📋 Wishlist"}</span>}
                <span style={{ fontSize: 14, color: "var(--color-text-tertiary)", transform: filterOpenSection === "show" ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
              </div>
            </div>
            {filterOpenSection === "show" && (
              <div style={{ padding: "10px 20px 12px", display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[{ id: "all", label: "All" }, { id: "visited", label: "✓ Visited" }, { id: "wishlist", label: "📋 Wishlist" }].map(f => (
                  <span key={f.id} onClick={() => { setTypeFilter(f.id); setFilterOpenSection(null); }} style={{
                    padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", userSelect: "none",
                    border: typeFilter === f.id ? "none" : "0.5px solid var(--color-border-secondary)",
                    background: typeFilter === f.id ? "#C94F2C" : "var(--color-background-primary)",
                    color: typeFilter === f.id ? "#fff" : "var(--color-text-secondary)",
                  }}>{f.label}</span>
                ))}
              </div>
            )}
          </div>

          {/* Venue type */}
          <div style={{ borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
            <div onClick={() => setFilterOpenSection(filterOpenSection === "venue" ? null : "venue")}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 20px", cursor: "pointer", background: "var(--color-background-secondary)" }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>Venue type</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {venueTypeFilter && <span style={{ fontSize: 12, color: "#C94F2C", fontWeight: 500 }}>{VENUE_TYPES.find(v => v.id === venueTypeFilter)?.emoji} {VENUE_TYPES.find(v => v.id === venueTypeFilter)?.label}</span>}
                <span style={{ fontSize: 14, color: "var(--color-text-tertiary)", transform: filterOpenSection === "venue" ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
              </div>
            </div>
            {filterOpenSection === "venue" && (
              <div style={{ padding: "10px 20px 12px", display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span onClick={() => { setVenueTypeFilter(""); setFilterOpenSection(null); }} style={{
                  padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", userSelect: "none",
                  border: venueTypeFilter === "" ? "none" : "0.5px solid var(--color-border-secondary)",
                  background: venueTypeFilter === "" ? "#C94F2C" : "var(--color-background-primary)",
                  color: venueTypeFilter === "" ? "#fff" : "var(--color-text-secondary)",
                }}>All</span>
                {VENUE_TYPES.map(v => (
                  <span key={v.id} onClick={() => { setVenueTypeFilter(venueTypeFilter === v.id ? "" : v.id); setFilterOpenSection(null); }} style={{
                    padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", userSelect: "none",
                    border: venueTypeFilter === v.id ? "none" : "0.5px solid var(--color-border-secondary)",
                    background: venueTypeFilter === v.id ? "#C94F2C" : "var(--color-background-primary)",
                    color: venueTypeFilter === v.id ? "#fff" : "var(--color-text-secondary)",
                  }}>{v.emoji} {v.label}</span>
                ))}
              </div>
            )}
          </div>

          {/* Minimum rating */}
          <div style={{ borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
            <div onClick={() => setFilterOpenSection(filterOpenSection === "rating" ? null : "rating")}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 20px", cursor: "pointer", background: "var(--color-background-secondary)" }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>Minimum rating</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {minStars > 0 && <span style={{ fontSize: 12, color: "#C94F2C", fontWeight: 500 }}>{minStars}★+</span>}
                <span style={{ fontSize: 14, color: "var(--color-text-tertiary)", transform: filterOpenSection === "rating" ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
              </div>
            </div>
            {filterOpenSection === "rating" && (
              <div style={{ padding: "10px 20px 12px", display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[0,1,2,3,4,5].map(s => (
                  <span key={s} onClick={() => { setMinStars(s); setFilterOpenSection(null); }} style={{
                    padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", userSelect: "none",
                    border: minStars === s ? "none" : "0.5px solid var(--color-border-secondary)",
                    background: minStars === s ? "#C94F2C" : "var(--color-background-primary)",
                    color: minStars === s ? "#fff" : "var(--color-text-secondary)",
                  }}>{s === 0 ? "All" : `${s}★+`}</span>
                ))}
              </div>
            )}
          </div>

          {/* Cuisine */}
          <div style={{ borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
            <div onClick={() => setFilterOpenSection(filterOpenSection === "cuisine" ? null : "cuisine")}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 20px", cursor: "pointer", background: "var(--color-background-secondary)" }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>Cuisine</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {cuisineFilter && <span style={{ fontSize: 12, color: "#C94F2C", fontWeight: 500 }}>{cuisineFilter}</span>}
                <span style={{ fontSize: 14, color: "var(--color-text-tertiary)", transform: filterOpenSection === "cuisine" ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
              </div>
            </div>
            {filterOpenSection === "cuisine" && (
              <div style={{ padding: "10px 20px 12px", display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span onClick={() => { setCuisineFilter(""); setFilterOpenSection(null); }} style={{
                  padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", userSelect: "none",
                  border: cuisineFilter === "" ? "none" : "0.5px solid var(--color-border-secondary)",
                  background: cuisineFilter === "" ? "#C94F2C" : "var(--color-background-primary)",
                  color: cuisineFilter === "" ? "#fff" : "var(--color-text-secondary)",
                }}>All</span>
                {CUISINES.filter(c => c !== "Other").map(c => (
                  <span key={c} onClick={() => { setCuisineFilter(cuisineFilter === c ? "" : c); setFilterOpenSection(null); }} style={{
                    padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", userSelect: "none",
                    border: cuisineFilter === c ? "none" : "0.5px solid var(--color-border-secondary)",
                    background: cuisineFilter === c ? "#C94F2C" : "var(--color-background-primary)",
                    color: cuisineFilter === c ? "#fff" : "var(--color-text-secondary)",
                  }}>{c}</span>
                ))}
              </div>
            )}
          </div>

          {hasActiveFilter && (
            <div style={{ padding: "10px 20px" }}>
              <button onClick={() => { setMinStars(0); setTypeFilter("all"); setVenueTypeFilter(""); setCuisineFilter(""); setShowPanel(false); setFilterOpenSection(null); }}
                style={{ fontSize: 13, color: "#C94F2C", background: "none", border: "none", cursor: "pointer", padding: "4px 0", fontWeight: 500 }}>
                Reset all filters
              </button>
            </div>
          )}
        </div>
      )}

      <div ref={scrollRef} style={styles.scrollBody} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {/* Pull to refresh indicator */}
        {refreshing && (
          <div style={{ display: "flex", justifyContent: "center", padding: "8px 0", marginBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--color-text-tertiary)" }}>
              <Spinner /> Refreshing...
            </div>
          </div>
        )}

        {/* Getting started checklist — shown to new users */}
        {!isSearching && (
          <GettingStartedCard
            visits={visits}
            wishlist={wishlist}
            friends={friendVisits.length}
            onAddSpot={onNewVisit}
            onAddFriend={() => onNavigate("friends")}
          />
        )}

        {/* Search bar — always rendered so focus is never lost */}
        <div style={{ marginBottom: 14 }}>
          <input
            style={styles.input}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search venues..."
            autoCapitalize="none"
            autoCorrect="off"
          />
        </div>

        {/* Search results */}
        {isSearching && searchResults.length === 0 && !loading && !friendsLoading && (
          <div style={{ textAlign: "center", padding: "48px 24px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
            <div style={styles.emptyH}>No results for "{query.trim()}"</div>
            <div style={styles.emptyP}>Try a different name or spelling. You can also add it as a new spot.</div>
            <button style={{ ...styles.btnPrimary, marginTop: 16 }} onClick={onNewVisit}>Add a Spot</button>
          </div>
        )}
        {!isSearching && (loading || friendsLoading) && <Spinner />}
        {isSearching && searchResults.map(r => {
          const avg = avgRating(r.allDishes);
          const uniqueDishes = getUniqueDishes(r.allDishes);
          const myCount = r.visits.filter(v => !v.isFriend).length;
          const friendCount = r.visits.filter(v => v.isFriend).length;
          const vEmoji = VENUE_TYPES.find(v => v.id === r.venue_type)?.emoji || "";
          return (
            <div key={r.key} style={styles.card} onClick={() => onOpenRestaurant(r.key)}>
              <div style={styles.cardHeader}>
                <div style={styles.initial}>{r.name[0].toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <div style={styles.restName}>{r.name}</div>
                    {vEmoji && <span style={{ fontSize: 14 }}>{vEmoji}</span>}
                    {myCount === 0 && friendCount > 0 && <span style={{ fontSize: 11, background: "#EAF4EA", color: "#3B6D11", borderRadius: 20, padding: "2px 8px", fontWeight: 500 }}>👥 Friends only</span>}
                    {myCount > 0 && friendCount > 0 && <span style={{ fontSize: 11, background: "#FAECE7", color: "#C94F2C", borderRadius: 20, padding: "2px 8px", fontWeight: 500 }}>👥 Friends too</span>}
                    {r.hasWishlist && myCount === 0 && friendCount === 0 && <span style={{ fontSize: 11, background: "#EEF2FF", color: "#4338CA", borderRadius: 20, padding: "2px 8px", fontWeight: 500 }}>📋 Wishlist</span>}
                  </div>
                  {r.address && <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginBottom: 2 }}>{r.address}</div>}
                  <div style={styles.cardSub}>
                    {myCount > 0 && `${myCount} your visit${myCount !== 1 ? "s" : ""}`}
                    {myCount > 0 && friendCount > 0 && " · "}
                    {friendCount > 0 && `${friendCount} friend visit${friendCount !== 1 ? "s" : ""}`}
                  </div>
                  {avg > 0 && <div style={{ marginTop: 3 }}><StarDisplay rating={Math.round(avg)} size={13} /><span style={{ fontSize: 12, color: "#888", marginLeft: 5 }}>{avg.toFixed(1)}</span></div>}
                </div>
              </div>
              <div style={styles.chips}>
                {uniqueDishes.slice(0, 5).map((d, i) => <span key={i} style={styles.chip}>{d.name}</span>)}
              </div>
            </div>
          );
        })}

        {!isSearching && !loading && !friendsLoading && feedItems.length === 0 && wishlist.length === 0 && (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>🍽</div>
            <div style={styles.emptyH}>No visits yet</div>
            <div style={styles.emptyP}>Tap "Add a Spot" to log your first visit.</div>
          </div>
        )}

        {!isSearching && feedItems.map((v) => {
          const isWishlist = !!v.isWishlist;
          const vEmoji = VENUE_TYPES.find(vt => vt.id === v.venue_type)?.emoji || "";
          const cardAvatar = v.isFriend ? v.friendAvatar : myProfile.avatar_url;
          const cardUsername = v.isFriend ? v.friendName : (getDisplayName(myProfile));

          if (isWishlist) {
            return (
              <div key={"wl-" + v.id + (v.isFriend ? "-f" : "")}
                style={{ ...styles.card, borderLeft: "3px solid #4338CA", cursor: v.isFriend ? "default" : "pointer" }}
                onClick={() => !v.isFriend && setEditingWishlist(v)}>
                <div style={styles.cardHeader}>
                  <Avatar url={cardAvatar} fallback={cardUsername?.[0]?.toUpperCase() || "?"} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
                      <span style={{ fontSize: 12, color: "var(--color-text-secondary)", fontWeight: 500 }}>{cardUsername}</span>
                      <span style={{ fontSize: 11, background: "#EEF2FF", color: "#4338CA", borderRadius: 20, padding: "1px 7px", fontWeight: 500 }}>📋 Wishlist</span>
                      {vEmoji && <span style={{ fontSize: 12 }}>{vEmoji}</span>}
                    </div>
                    <div style={styles.restName}>{v.rest}</div>
                    {v.address && <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 1 }}>{v.address}</div>}
                    <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 1 }}>
                      {new Date(v.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                    {v.note && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2, fontStyle: "italic" }}>"{v.note}"</div>}
                  </div>
                  {!v.isFriend && <span style={{ fontSize: 18, color: "var(--color-text-tertiary)", marginLeft: 4, flexShrink: 0 }}>›</span>}
                </div>
              </div>
            );
          }

          const avg = avgRating(v.dishes);
          const ds = new Date(v.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
          return (
            <div key={v.id + (v.isFriend ? "-f" : "")} style={styles.card} onClick={() => v.isFriend ? onOpenRestaurant(v.place_id || v.rest) : onOpenVisit(v.id)}>
              <div style={styles.cardHeader}>
                <Avatar url={cardAvatar} fallback={cardUsername?.[0]?.toUpperCase() || "?"} size={40} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
                    <span style={{ fontSize: 12, color: "var(--color-text-secondary)", fontWeight: 500 }}>{cardUsername}</span>
                    {vEmoji && <span style={{ fontSize: 12 }}>{vEmoji}</span>}
                  </div>
                  <div style={styles.restName}>{v.rest}</div>
                  {v.address && <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 1 }}>{v.address}</div>}
                  <div style={styles.cardSub}>
                    {ds} · {v.dishes.length} item{v.dishes.length !== 1 ? "s" : ""}
                    {v.cuisine && <span style={{ marginLeft: 6, padding: "1px 7px", borderRadius: 20, background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)", fontSize: 10 }}>{v.cuisine}</span>}
                    {v.dining_type && <span style={{ marginLeft: 6, padding: "1px 7px", borderRadius: 20, background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)", fontSize: 10 }}>{v.dining_type}</span>}
                  </div>
                  {avg > 0 && <div style={{ marginTop: 3 }}><StarDisplay rating={Math.round(avg)} size={13} /><span style={{ fontSize: 12, color: "#888", marginLeft: 5 }}>{avg.toFixed(1)}</span></div>}
                </div>
                <span style={{ fontSize: 18, color: "var(--color-text-tertiary)", marginLeft: 4, flexShrink: 0 }}>›</span>
              </div>
              <div style={{ ...styles.chips, justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
                  {v.dishes.slice(0, 5).map((d, i) => <span key={i} style={styles.chip}>{d.name}</span>)}
                </div>
                <ChefKissButton targetType="visit" targetId={v.id} user={user} size={20} />
              </div>
            </div>
          );
        })}

        <div style={{ height: 16 }} />
      </div>
      <BottomNav active="venues" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}
const VENUE_TYPES = [
  { id: "restaurant", label: "Restaurant", emoji: "🍽" },
  { id: "brewery", label: "Brewery", emoji: "🍺" },
  { id: "winery", label: "Winery", emoji: "🍷" },
  { id: "bar", label: "Bar", emoji: "🍸" },
];

const BEER_TYPES = ["IPA", "Stout", "Lager", "Sour", "Pale Ale", "Porter", "Wheat", "Saison", "Belgian", "Pilsner", "Amber", "Brown Ale", "Barleywine", "Other"];
const WINE_TYPES = ["Red", "White", "Rosé", "Sparkling", "Dessert", "Orange", "Natural"];
const BASE_SPIRITS = ["Vodka", "Gin", "Rum", "Whiskey", "Tequila", "Mezcal", "Bourbon", "Brandy", "Champagne", "Aperol", "Amaro", "Many/Mixed"];
const BEV_TYPES = ["Beer", "Wine", "Cocktail"];

function PillSelector({ options, value, onChange }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {options.map(o => (
        <span key={o} onClick={() => onChange(value === o ? "" : o)} style={{
          padding: "6px 13px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", userSelect: "none",
          border: value === o ? "none" : "0.5px solid var(--color-border-secondary)",
          background: value === o ? "#C94F2C" : "var(--color-background-secondary)",
          color: value === o ? "#fff" : "var(--color-text-secondary)",
        }}>{o}</span>
      ))}
    </div>
  );
}

function AddVisitScreen({ onSave, onCancel, user, onNavigate, friendRequestCount }) {
  const [mode, setMode] = useState("rate"); // "rate" | "wishlist"

  // Add a Spot state
  const [venueType, setVenueType] = useState("restaurant");
  const [restaurant, setRestaurant] = useState(null);
  const [date, setDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [diningType, setDiningType] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [cuisineOther, setCuisineOther] = useState("");
  const [openSection, setOpenSection] = useState(null); // "dining" | "cuisine"
  const [pendingDishes, setPendingDishes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [itemMode, setItemMode] = useState("food");

  // Food fields
  const [dbName, setDbName] = useState("");
  const [dbRating, setDbRating] = useState(0);
  const [dbCat, setDbCat] = useState("Appetizer");
  const [dbComment, setDbComment] = useState("");
  const [dbPhotoFile, setDbPhotoFile] = useState(null);
  const [dbPhotoPreview, setDbPhotoPreview] = useState(null);

  // Beverage fields
  const [bevType, setBevType] = useState("");
  const [bevName, setBevName] = useState("");
  const [bevRating, setBevRating] = useState(0);
  const [bevStyle, setBevStyle] = useState("");
  const [bevAbv, setBevAbv] = useState("");
  const [bevNotes, setBevNotes] = useState("");
  const [bevPhotoFile, setBevPhotoFile] = useState(null);
  const [bevPhotoPreview, setBevPhotoPreview] = useState(null);
  const [bevOpenSection, setBevOpenSection] = useState(null);

  // Wishlist state
  const [wlRestaurant, setWlRestaurant] = useState(null);
  const [wlVenueType, setWlVenueType] = useState("restaurant");
  const [wlNote, setWlNote] = useState("");

  function resetFood() { setDbName(""); setDbRating(0); setDbCat("Appetizer"); setDbComment(""); setDbPhotoFile(null); setDbPhotoPreview(null); }
  function resetBev() { setBevName(""); setBevRating(0); setBevStyle(""); setBevAbv(""); setBevNotes(""); setBevType(""); setBevPhotoFile(null); setBevPhotoPreview(null); setBevOpenSection(null); }

  function addFood() {
    if (!dbName.trim()) return;
    setPendingDishes([...pendingDishes, { name: dbName.trim(), rating: dbRating, cat: dbCat, comment: dbComment.trim(), _photoFile: dbPhotoFile, _photoPreview: dbPhotoPreview }]);
    resetFood();
  }

  function addBeverage() {
    if (!bevName.trim()) return;
    if (!bevType) { alert("Please select Beer, Wine, or Cocktail."); return; }
    const metaParts = [bevStyle, bevType === "Beer" && bevAbv ? `${bevAbv}% ABV` : null].filter(Boolean);
    setPendingDishes([...pendingDishes, {
      name: bevName.trim(), rating: bevRating, cat: bevType,
      comment: bevNotes.trim(), photo: null,
      beer_type: bevStyle || null,
      abv: bevType === "Beer" && bevAbv ? parseFloat(bevAbv) : null,
      _meta: metaParts.join(" · "),
      _photoFile: bevPhotoFile,
      _photoPreview: bevPhotoPreview,
    }]);
    resetBev();
  }

  function removeDish(i) { setPendingDishes(pendingDishes.filter((_, idx) => idx !== i)); }

  function handlePhoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    setDbPhotoFile(file);
    setDbPhotoPreview(URL.createObjectURL(file));
  }

  async function handleSave() {
    if (!restaurant) { alert("Please select a venue."); return; }
    if (!pendingDishes.length) { alert("Add at least one item first."); return; }
    setSaving(true);
    try {
      const { data: visitData, error: visitError } = await supabase
        .from("visits").insert({
          rest: restaurant.name, address: restaurant.address || null,
          place_id: restaurant.place_id || null, date,
          dining_type: diningType || null, venue_type: venueType, user_id: user.id,
          cuisine: cuisine === "Other" ? (cuisineOther.trim() || null) : (cuisine || null),
        }).select().single();
      if (visitError) throw visitError;
      const dishRows = await Promise.all(pendingDishes.map(async d => {
        let photoUrl = null;
        if (d._photoFile) photoUrl = await uploadPhoto(d._photoFile, user.id);
        return { visit_id: visitData.id, name: d.name, rating: d.rating, cat: d.cat, comment: d.comment, photo: photoUrl, beer_type: d.beer_type || null, abv: d.abv || null, user_id: user.id };
      }));
      const { error: dishError } = await supabase.from("dishes").insert(dishRows);
      if (dishError) throw dishError;
      onSave(visitData);
    } catch (err) { alert("Error saving: " + err.message); }
    finally { setSaving(false); }
  }

  async function handleWishlistSave() {
    if (!wlRestaurant) { alert("Please select a venue."); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from("waitlist").insert({
        user_id: user.id,
        rest: wlRestaurant.name,
        address: wlRestaurant.address || null,
        place_id: wlRestaurant.place_id || null,
        note: wlNote.trim() || null,
        venue_type: wlVenueType,
      });
      if (error) throw error;
      onSave();
    } catch (err) { alert("Error saving: " + err.message); }
    finally { setSaving(false); }
  }

  const styleOptions = bevType === "Beer" ? BEER_TYPES : bevType === "Wine" ? WINE_TYPES : bevType === "Cocktail" ? BASE_SPIRITS : [];
  const styleLabel = bevType === "Beer" ? "Beer type" : bevType === "Wine" ? "Wine type" : bevType === "Cocktail" ? "Base spirit" : "";

  return (
    <div style={styles.screen}>
      <TopBar subtitle="Add" left={<button style={styles.backBtn} onClick={onCancel}>‹ Back</button>} />
      <div style={styles.scrollBody}>

        {/* Mode picker */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", background: "var(--color-background-secondary)", borderRadius: 12, padding: 4, border: "0.5px solid var(--color-border-secondary)" }}>
            <div onClick={() => setMode("rate")} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: "pointer", background: mode === "rate" ? "#C94F2C" : "transparent", color: mode === "rate" ? "#fff" : "var(--color-text-secondary)", transition: "all 0.15s" }}>
              🍽 Rate Your Plate
            </div>
            <div onClick={() => setMode("wishlist")} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: "pointer", background: mode === "wishlist" ? "#C94F2C" : "transparent", color: mode === "wishlist" ? "#fff" : "var(--color-text-secondary)", transition: "all 0.15s" }}>
              📋 Wishlist
            </div>
          </div>
        </div>

        {/* ── WISHLIST MODE ── */}
        {mode === "wishlist" && (
          <>
            <div style={styles.formSection}>
              <div style={styles.formLabel}>Venue</div>
              <RestaurantSearch onSelect={setWlRestaurant} />
            </div>
            <div style={styles.formSection}>
              <div style={styles.formLabel}>Type of venue</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "nowrap", overflowX: "auto" }}>
                {VENUE_TYPES.map(v => (
                  <span key={v.id} onClick={() => setWlVenueType(v.id)} style={{
                    padding: "8px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500,
                    cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", flexShrink: 0,
                    border: wlVenueType === v.id ? "none" : "0.5px solid var(--color-border-secondary)",
                    background: wlVenueType === v.id ? "#C94F2C" : "var(--color-background-secondary)",
                    color: wlVenueType === v.id ? "#fff" : "var(--color-text-secondary)",
                  }}>{v.emoji} {v.label}</span>
                ))}
              </div>
            </div>
            <div style={styles.formSection}>
              <div style={styles.formLabel}>Note (optional)</div>
              <input style={styles.input} value={wlNote} onChange={(e) => setWlNote(e.target.value)} placeholder="e.g. Try the pasta, go on weekends" />
            </div>
            <button style={{ ...styles.saveVisitBtn, opacity: saving ? 0.7 : 1 }} onClick={handleWishlistSave} disabled={saving}>
              {saving ? "Saving..." : "Add to Wishlist"}
            </button>
          </>
        )}

        {/* ── RATE A PLATE MODE ── */}
        {mode === "rate" && (
          <>
            <div style={styles.formSection}>
              <div style={styles.formLabel}>{VENUE_TYPES.find(v => v.id === venueType)?.label || "Venue"}</div>
              <RestaurantSearch onSelect={setRestaurant} />
            </div>

            <div style={styles.formSection}>
              <div style={styles.formLabel}>Date</div>
              <input style={styles.input} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>

            <div style={styles.formSection}>
              <div style={styles.formLabel}>Type of venue</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "nowrap", overflowX: "auto" }}>
                {VENUE_TYPES.map(v => (
                  <span key={v.id} onClick={() => { setVenueType(v.id); setItemMode(v.id === "restaurant" ? "food" : "beverage"); }} style={{
                    padding: "8px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500,
                    cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", flexShrink: 0,
                    border: venueType === v.id ? "none" : "0.5px solid var(--color-border-secondary)",
                    background: venueType === v.id ? "#C94F2C" : "var(--color-background-secondary)",
                    color: venueType === v.id ? "#fff" : "var(--color-text-secondary)",
                  }}>{v.emoji} {v.label}</span>
                ))}
              </div>
            </div>

            {/* Collapsible: Dining Type */}
            <div style={{ borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", marginBottom: 10, overflow: "hidden" }}>
              <div onClick={() => setOpenSection(openSection === "dining" ? null : "dining")}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", cursor: "pointer", background: "var(--color-background-secondary)" }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>Dining type</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {diningType && <span style={{ fontSize: 12, color: "#C94F2C", fontWeight: 500 }}>{diningType}</span>}
                  <span style={{ fontSize: 14, color: "var(--color-text-tertiary)", transform: openSection === "dining" ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
                </div>
              </div>
              {openSection === "dining" && (
                <div style={{ padding: "12px 14px", display: "flex", gap: 8 }}>
                  {["Dine In", "Take Out", "Delivery"].map(t => (
                    <span key={t} onClick={() => { setDiningType(diningType === t ? "" : t); setOpenSection(null); }} style={{
                      flex: 1, textAlign: "center", padding: "10px 6px", borderRadius: 10, fontSize: 13, fontWeight: 500,
                      cursor: "pointer", userSelect: "none",
                      border: diningType === t ? "none" : "0.5px solid var(--color-border-secondary)",
                      background: diningType === t ? "#C94F2C" : "var(--color-background-primary)",
                      color: diningType === t ? "#fff" : "var(--color-text-secondary)",
                    }}>{t}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Collapsible: Cuisine */}
            <div style={{ borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", marginBottom: 16, overflow: "hidden" }}>
              <div onClick={() => setOpenSection(openSection === "cuisine" ? null : "cuisine")}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", cursor: "pointer", background: "var(--color-background-secondary)" }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>Cuisine</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {(cuisine === "Other" ? cuisineOther : cuisine) && <span style={{ fontSize: 12, color: "#C94F2C", fontWeight: 500 }}>{cuisine === "Other" ? cuisineOther : cuisine}</span>}
                  <span style={{ fontSize: 14, color: "var(--color-text-tertiary)", transform: openSection === "cuisine" ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
                </div>
              </div>
              {openSection === "cuisine" && (
                <div style={{ padding: "12px 14px" }}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: cuisine === "Other" ? 8 : 0 }}>
                    {CUISINES.map(c => (
                      <span key={c} onClick={() => { setCuisine(cuisine === c ? "" : c); if (c !== "Other") { setCuisineOther(""); setOpenSection(null); } }} style={{
                        padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                        cursor: "pointer", userSelect: "none",
                        border: cuisine === c ? "none" : "0.5px solid var(--color-border-secondary)",
                        background: cuisine === c ? "#C94F2C" : "var(--color-background-primary)",
                        color: cuisine === c ? "#fff" : "var(--color-text-secondary)",
                      }}>{c}</span>
                    ))}
                  </div>
                  {cuisine === "Other" && (
                    <input style={{ ...styles.input, marginTop: 8 }} value={cuisineOther}
                      onChange={e => setCuisineOther(e.target.value)} placeholder="e.g. Peruvian, Fusion..."
                      onBlur={() => setOpenSection(null)} />
                  )}
                </div>
              )}
            </div>

            <div style={styles.divider} />

            {/* Added items */}
            {pendingDishes.length > 0 && (
              <>
                <div style={{ ...styles.formLabel, marginBottom: 10 }}>Added</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                  {pendingDishes.map((d, i) => (
                    <div key={i} style={styles.loggedDish}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 3 }}>{d.name}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          {d.rating > 0 ? <StarDisplay rating={d.rating} size={12} /> : <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>No rating</span>}
                          <span style={styles.catBadge}>{d.cat}</span>
                          {d._meta && <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{d._meta}</span>}
                        </div>
                      </div>
                      <button style={styles.removeBtn} onClick={() => removeDish(i)}>×</button>
                    </div>
                  ))}
                </div>
                <div style={styles.divider} />
              </>
            )}

            {/* Food/Beverage toggle */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {["food", "beverage"].map(m => (
                <span key={m} onClick={() => setItemMode(m)} style={{
                  flex: 1, textAlign: "center", padding: "10px 6px", borderRadius: 10, fontSize: 13, fontWeight: 500,
                  cursor: "pointer", userSelect: "none",
                  border: itemMode === m ? "none" : "0.5px solid var(--color-border-secondary)",
                  background: itemMode === m ? "#C94F2C" : "var(--color-background-secondary)",
                  color: itemMode === m ? "#fff" : "var(--color-text-secondary)",
                }}>{m === "food" ? "🍽 Add food" : "🥤 Add a beverage"}</span>
              ))}
            </div>

            {/* Beverage builder */}
            {itemMode === "beverage" && (
              <div style={styles.dishBuilder}>
                <div style={styles.dbField}>
                  <div style={styles.dbLabel}>Beverage type</div>
                  <PillSelector options={BEV_TYPES} value={bevType} onChange={v => { setBevType(v); setBevOpenSection(null); setBevStyle(""); }} />
                </div>
                {bevType && (
                  <>
                    <div style={styles.dbField}>
                      <div style={styles.dbLabel}>Name</div>
                      <input style={styles.input} value={bevName} onChange={(e) => setBevName(e.target.value)}
                        placeholder={bevType === "Beer" ? "e.g. Hazy IPA" : bevType === "Wine" ? "e.g. Cabernet Sauvignon" : "e.g. Old Fashioned"}
                        onKeyDown={(e) => e.key === "Enter" && addBeverage()} />
                    </div>
                    <div style={styles.dbField}>
                      <div style={styles.dbLabel}>Rating</div>
                      <StarPicker value={bevRating} onChange={setBevRating} />
                    </div>

                    {/* Collapsible style picker */}
                    {styleOptions.length > 0 && (
                      <div style={{ borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", marginBottom: 10, overflow: "hidden" }}>
                        <div onClick={() => setBevOpenSection(bevOpenSection === "style" ? null : "style")}
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", cursor: "pointer", background: "var(--color-background-secondary)" }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>{styleLabel}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {bevStyle && <span style={{ fontSize: 12, color: "#C94F2C", fontWeight: 500 }}>{bevStyle}</span>}
                            <span style={{ fontSize: 14, color: "var(--color-text-tertiary)", transform: bevOpenSection === "style" ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
                          </div>
                        </div>
                        {bevOpenSection === "style" && (
                          <div style={{ padding: "12px 14px", display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {styleOptions.map(s => (
                              <span key={s} onClick={() => { setBevStyle(bevStyle === s ? "" : s); setBevOpenSection(null); }} style={{
                                padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                                cursor: "pointer", userSelect: "none",
                                border: bevStyle === s ? "none" : "0.5px solid var(--color-border-secondary)",
                                background: bevStyle === s ? "#C94F2C" : "var(--color-background-primary)",
                                color: bevStyle === s ? "#fff" : "var(--color-text-secondary)",
                              }}>{s}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Collapsible ABV for beer */}
                    {bevType === "Beer" && (
                      <div style={{ borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", marginBottom: 10, overflow: "hidden" }}>
                        <div onClick={() => setBevOpenSection(bevOpenSection === "abv" ? null : "abv")}
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", cursor: "pointer", background: "var(--color-background-secondary)" }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>ABV %</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {bevAbv && <span style={{ fontSize: 12, color: "#C94F2C", fontWeight: 500 }}>{bevAbv}%</span>}
                            <span style={{ fontSize: 14, color: "var(--color-text-tertiary)", transform: bevOpenSection === "abv" ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
                          </div>
                        </div>
                        {bevOpenSection === "abv" && (
                          <div style={{ padding: "12px 14px" }}>
                            <input style={{ ...styles.input, width: "50%" }} type="number" min="0" max="100" step="0.1"
                              value={bevAbv} onChange={(e) => setBevAbv(e.target.value)} placeholder="e.g. 6.5"
                              autoFocus />
                          </div>
                        )}
                      </div>
                    )}

                    <div style={styles.dbField}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                        <div style={{ ...styles.dbLabel, marginBottom: 0 }}>Comments</div>
                        <label style={{ ...styles.photoPill, padding: "6px 12px", fontSize: 12 }}>
                          <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) { setBevPhotoFile(f); setBevPhotoPreview(URL.createObjectURL(f)); } }} />
                          {bevPhotoPreview ? <><img src={bevPhotoPreview} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: "cover" }} /> Added</> : "📷 Add photo"}
                        </label>
                      </div>
                      <textarea style={{ ...styles.input, resize: "none", fontSize: 13 }} rows={2} value={bevNotes} onChange={(e) => setBevNotes(e.target.value)} placeholder="How was it?" />
                    </div>
                    <button style={styles.addDishBtn} onClick={addBeverage}>Add beverage</button>
                  </>
                )}
              </div>
            )}

            {/* Food builder */}
            {itemMode === "food" && (
              <div style={styles.dishBuilder}>
                <div style={styles.dbField}>
                  <div style={styles.dbLabel}>Dish name</div>
                  <input style={styles.input} value={dbName} onChange={(e) => setDbName(e.target.value)} placeholder="e.g. Truffle tagliatelle" onKeyDown={(e) => e.key === "Enter" && addFood()} />
                </div>
                <div style={styles.dbField}>
                  <div style={styles.dbLabel}>Rating</div>
                  <StarPicker value={dbRating} onChange={setDbRating} />
                </div>
                <div style={styles.dbField}>
                  <div style={styles.dbLabel}>Category</div>
                  <PillSelector options={CATS} value={dbCat} onChange={(v) => setDbCat(v || "Appetizer")} />
                </div>
                <div style={styles.dbField}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ ...styles.dbLabel, marginBottom: 0 }}>Comments</div>
                    <label style={{ ...styles.photoPill, padding: "6px 12px", fontSize: 12 }}>
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhoto} />
                      {dbPhotoPreview ? <><img src={dbPhotoPreview} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: "cover" }} /> Added</> : "📷 Add photo"}
                    </label>
                  </div>
                  <textarea style={{ ...styles.input, resize: "none", fontSize: 13 }} rows={2} value={dbComment} onChange={(e) => setDbComment(e.target.value)} placeholder="How was it? Would you order again?" />
                </div>
                <button style={styles.addDishBtn} onClick={addFood}>Add dish</button>
              </div>
            )}

            <div style={styles.divider} />
            <button style={{ ...styles.saveVisitBtn, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save visit"}
            </button>
            <div style={{ height: 16 }} />
          </>
        )}

      </div>
      <BottomNav active="venues" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}

// ── DETAIL SCREEN ─────────────────────────────────────────
function DetailScreen({ visit, onBack, onEdit, onDelete, onNavigate, friendRequestCount, user }) {
  const [dishes, setDishes] = useState(visit.dishes);

  // Load full dish data including photos when detail opens
  useEffect(() => {
    supabase.from("dishes").select(DISH_FIELDS).eq("visit_id", visit.id).then(({ data }) => {
      if (data) setDishes(data);
    });
  }, [visit.id]);

  const avg = avgRating(dishes);
  const ds = new Date(visit.date + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "long", day: "numeric", year: "numeric",
  });
  const byCat = {};
  CATS.forEach((c) => {
    const items = dishes.filter((d) => d.cat === c);
    if (items.length) byCat[c] = items;
  });

  return (
    <div style={styles.screen}>
      <TopBar
        subtitle={visit.rest}
        left={<button style={styles.backBtn} onClick={onBack}>‹ Visits</button>}
        right={<div style={{ display: "flex", gap: 8 }}>
          <button style={styles.actionBtnBlue} onClick={() => onEdit(visit.id)}>Edit</button>
          <button style={styles.actionBtnRed} onClick={() => onDelete(visit.id)}>Delete</button>
        </div>}
      />
      <div style={styles.scrollBody}>
        <div style={styles.detailHeader}>
          <div style={styles.detailRest}>{visit.rest}</div>
          {visit.address && (
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 4 }}>
              {visit.address}
            </div>
          )}
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 6, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
            {ds}
            {visit.cuisine && <span style={{ padding: "2px 8px", borderRadius: 20, background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)", fontSize: 11 }}>{visit.cuisine}</span>}
            {visit.dining_type && <span style={{ padding: "2px 8px", borderRadius: 20, background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)", fontSize: 11 }}>{visit.dining_type}</span>}
          </div>
          {avg > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <StarDisplay rating={Math.round(avg)} size={15} />
              <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                {avg.toFixed(1)} avg · {visit.dishes.length} item{visit.dishes.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <ChefKissButton targetType="visit" targetId={visit.id} user={user} size={22} />
          </div>

          {/* Survey results */}
          {(visit.would_return || visit.wait_time || visit.service_rating || visit.occasion || visit.would_recommend) && (
            <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {visit.would_return && <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: visit.would_return === "Yes" ? "#EAF4EA" : visit.would_return === "No" ? "#FFF0F0" : "#FFF5F2", color: visit.would_return === "Yes" ? "#3B6D11" : visit.would_return === "No" ? "#C94F2C" : "#9A5C00", fontWeight: 500 }}>🔄 Return: {visit.would_return}</span>}
              {visit.wait_time && <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "var(--color-background-secondary)", color: "var(--color-text-secondary)", fontWeight: 500 }}>⏱ Wait: {visit.wait_time}</span>}
              {visit.service_rating > 0 && <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "var(--color-background-secondary)", color: "var(--color-text-secondary)", fontWeight: 500 }}>{"⭐".repeat(visit.service_rating)} Service</span>}
              {visit.occasion && <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "var(--color-background-secondary)", color: "var(--color-text-secondary)", fontWeight: 500 }}>🎉 {visit.occasion}</span>}
              {visit.would_recommend && <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: visit.would_recommend === "Yes" ? "#EAF4EA" : visit.would_recommend === "No" ? "#FFF0F0" : "#FFF5F2", color: visit.would_recommend === "Yes" ? "#3B6D11" : visit.would_recommend === "No" ? "#C94F2C" : "#9A5C00", fontWeight: 500 }}>👍 Recommend: {visit.would_recommend}</span>}
            </div>
          )}
        </div>

        {Object.entries(byCat).map(([cat, dishes]) => (
          <div key={cat} style={{ marginBottom: 16 }}>
            <div style={styles.catGroupLabel}>
              <span style={styles.catDot} />
              {cat}
            </div>
            {dishes.map((d, i) => (
              <div key={i} style={styles.detailDish}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>{d.name}</div>
                  <ChefKissButton targetType="dish" targetId={d.id} user={user} size={18} />
                </div>
                {(d.beer_type || d.abv) && (
                  <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 4 }}>
                    {[d.beer_type, d.abv ? `${d.abv}% ABV` : null].filter(Boolean).join(" · ")}
                  </div>
                )}
                {d.rating > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: d.comment ? 4 : 0 }}>
                    <StarDisplay rating={d.rating} size={14} />
                    <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{LABELS[d.rating]}</span>
                  </div>
                )}
                {photoSrc(d.photo) && (
                  <img src={photoSrc(d.photo)} alt={d.name} style={{ width: "100%", borderRadius: 8, marginBottom: d.comment ? 6 : 0, maxHeight: 180, objectFit: "cover" }} />
                )}
                {d.comment && (
                  <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>{d.comment}</div>
                )}
              </div>
            ))}
          </div>
        ))}
        <div style={{ height: 16 }} />
      </div>
      <BottomNav active="venues" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}

// ── EDIT VISIT SCREEN ────────────────────────────────────
function EditVisitScreen({ visit, onSave, onCancel, user, onNavigate, friendRequestCount }) {
  const [rest, setRest] = useState(visit.rest);
  const [date, setDate] = useState(visit.date);
  const [diningType, setDiningType] = useState(visit.dining_type || "");
  const [cuisine, setCuisine] = useState(() => {
    if (!visit.cuisine) return "";
    return CUISINES.includes(visit.cuisine) ? visit.cuisine : "Other";
  });
  const [cuisineOther, setCuisineOther] = useState(() => {
    if (!visit.cuisine) return "";
    return CUISINES.includes(visit.cuisine) ? "" : visit.cuisine;
  });
  const [venueType, setVenueType] = useState(visit.venue_type || "restaurant");
  const [dishes, setDishes] = useState(visit.dishes.map(d => ({ ...d })));
  const [saving, setSaving] = useState(false);

  // mode: null | "edit-dish" | "add-food" | "add-beverage"
  const [mode, setMode] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  // Food add fields
  const [dbName, setDbName] = useState("");
  const [dbRating, setDbRating] = useState(0);
  const [dbCat, setDbCat] = useState("Appetizer");
  const [dbComment, setDbComment] = useState("");
  const [dbPhotoFile, setDbPhotoFile] = useState(null);
  const [dbPhotoPreview, setDbPhotoPreview] = useState(null);

  // Beverage add fields
  const [bevType, setBevType] = useState("");
  const [bevName, setBevName] = useState("");
  const [bevRating, setBevRating] = useState(0);
  const [bevStyle, setBevStyle] = useState("");
  const [bevAbv, setBevAbv] = useState("");
  const [bevNotes, setBevNotes] = useState("");

  function openEditDish(i) { setEditingIndex(i); setMode("edit-dish"); }
  function updateDish(field, value) {
    setDishes(dishes.map((d, idx) => idx === editingIndex ? { ...d, [field]: value } : d));
  }
  function removeDish(i) { setDishes(dishes.filter((_, idx) => idx !== i)); setMode(null); setEditingIndex(null); }

  function handleDishPhoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    updateDish("_photoFile", file);
    updateDish("_photoPreview", URL.createObjectURL(file));
  }

  function handleNewPhoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    setDbPhotoFile(file);
    setDbPhotoPreview(URL.createObjectURL(file));
  }

  function addFood() {
    if (!dbName.trim()) return;
    setDishes([...dishes, { name: dbName.trim(), rating: dbRating, cat: dbCat, comment: dbComment.trim(), _photoFile: dbPhotoFile, _photoPreview: dbPhotoPreview }]);
    setDbName(""); setDbRating(0); setDbCat("Appetizer"); setDbComment(""); setDbPhotoFile(null); setDbPhotoPreview(null);
    setMode(null);
  }

  function addBeverage() {
    if (!bevName.trim()) return;
    if (!bevType) { alert("Please select Beer, Wine, or Cocktail."); return; }
    const metaParts = [bevStyle, bevType === "Beer" && bevAbv ? `${bevAbv}% ABV` : null].filter(Boolean);
    setDishes([...dishes, {
      name: bevName.trim(), rating: bevRating, cat: bevType,
      comment: bevNotes.trim(), photo: null,
      beer_type: bevStyle || null,
      abv: bevType === "Beer" && bevAbv ? parseFloat(bevAbv) : null,
      _meta: metaParts.join(" · "),
    }]);
    setBevName(""); setBevRating(0); setBevType(""); setBevStyle(""); setBevAbv(""); setBevNotes("");
    setMode(null);
  }

  async function handleSave() {
    if (!rest.trim()) { alert("Please enter a restaurant name."); return; }
    setSaving(true);
    try {
      const { error: visitError } = await supabase.from("visits")
        .update({ rest: rest.trim(), date, dining_type: diningType || null, venue_type: venueType,
          cuisine: cuisine === "Other" ? (cuisineOther.trim() || null) : (cuisine || null) })
        .eq("id", visit.id);
      if (visitError) throw visitError;

      const { error: deleteError } = await supabase.from("dishes").delete().eq("visit_id", visit.id);
      if (deleteError) throw deleteError;

      if (dishes.length > 0) {
        const dishRows = await Promise.all(dishes.map(async (d) => {
          let photoUrl = d.photo || null;
          if (d._photoFile) photoUrl = await uploadPhoto(d._photoFile, user.id);
          return {
            visit_id: visit.id, name: d.name, rating: d.rating,
            cat: d.cat, comment: d.comment, photo: photoUrl,
            beer_type: d.beer_type || null, abv: d.abv || null, user_id: user.id,
          };
        }));
        const { error: insertError } = await supabase.from("dishes").insert(dishRows);
        if (insertError) throw insertError;
      }
      onSave();
    } catch (err) {
      alert("Error saving: " + err.message);
    } finally { setSaving(false); }
  }

  const editingDish = editingIndex !== null ? dishes[editingIndex] : null;
  const isBevDish = editingDish && ["Beer", "Wine", "Cocktail"].includes(editingDish.cat);
  const styleOptions = bevType === "Beer" ? BEER_TYPES : bevType === "Wine" ? WINE_TYPES : bevType === "Cocktail" ? BASE_SPIRITS : [];

  return (
    <div style={styles.screen}>
      <TopBar subtitle="Edit visit" left={<button style={styles.backBtn} onClick={mode ? () => setMode(null) : onCancel}>‹ {mode ? "Back" : "Cancel"}</button>} />
      <div style={styles.scrollBody}>

        {/* ── EDIT EXISTING DISH/BEVERAGE ── */}
        {mode === "edit-dish" && editingDish && (
          <>
            <div style={{ ...styles.formLabel, marginBottom: 12 }}>Editing: {editingDish.name}</div>
            <div style={styles.dishBuilder}>
              <div style={styles.dbField}>
                <div style={styles.dbLabel}>{isBevDish ? "Name" : "Dish name"}</div>
                <input style={styles.input} value={editingDish.name} onChange={(e) => updateDish("name", e.target.value)} />
              </div>
              <div style={styles.dbField}>
                <div style={styles.dbLabel}>Rating</div>
                <StarPicker value={editingDish.rating} onChange={(v) => updateDish("rating", v)} />
              </div>
              {isBevDish ? (
                <>
                  <div style={styles.dbField}>
                    <div style={styles.dbLabel}>Type</div>
                    <PillSelector options={BEV_TYPES} value={editingDish.cat} onChange={(v) => updateDish("cat", v || editingDish.cat)} />
                  </div>
                  {editingDish.cat === "Beer" && (
                    <>
                      <div style={styles.dbField}>
                        <div style={styles.dbLabel}>Beer type</div>
                        <PillSelector options={BEER_TYPES} value={editingDish.beer_type || ""} onChange={(v) => updateDish("beer_type", v)} />
                      </div>
                      <div style={styles.dbField}>
                        <div style={styles.dbLabel}>ABV %</div>
                        <input style={{ ...styles.input, width: "50%" }} type="number" min="0" max="100" step="0.1" value={editingDish.abv || ""} onChange={(e) => updateDish("abv", e.target.value)} placeholder="e.g. 6.5" />
                      </div>
                    </>
                  )}
                  {editingDish.cat === "Wine" && (
                    <div style={styles.dbField}>
                      <div style={styles.dbLabel}>Wine type</div>
                      <PillSelector options={WINE_TYPES} value={editingDish.beer_type || ""} onChange={(v) => updateDish("beer_type", v)} />
                    </div>
                  )}
                  {editingDish.cat === "Cocktail" && (
                    <div style={styles.dbField}>
                      <div style={styles.dbLabel}>Base spirit</div>
                      <PillSelector options={BASE_SPIRITS} value={editingDish.beer_type || ""} onChange={(v) => updateDish("beer_type", v)} />
                    </div>
                  )}
                </>
              ) : (
                <div style={styles.dbField}>
                  <div style={styles.dbLabel}>Category</div>
                  <PillSelector options={CATS} value={editingDish.cat} onChange={(v) => updateDish("cat", v || editingDish.cat)} />
                </div>
              )}
              {!isBevDish && (
                <div style={styles.dbField}>
                  <div style={styles.dbLabel}>Photo</div>
                  <label style={styles.photoPill}>
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleDishPhoto} />
                    {(editingDish._photoPreview || editingDish.photo) ? <><img src={editingDish._photoPreview || editingDish.photo} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover" }} /> Photo added</> : "📷 Add photo"}
                  </label>
                </div>
              )}
              <div style={{ ...styles.dbField, marginBottom: 8 }}>
                <div style={styles.dbLabel}>Comments</div>
                <textarea style={{ ...styles.input, resize: "none", fontSize: 13 }} rows={2} value={editingDish.comment || ""} onChange={(e) => updateDish("comment", e.target.value)} placeholder="How was it?" />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button style={styles.saveVisitBtn} onClick={() => setMode(null)}>Save changes</button>
              <button style={{ ...styles.saveVisitBtn, background: "#E24B4A", flex: "0 0 auto", width: "auto", padding: "14px 20px" }} onClick={() => removeDish(editingIndex)}>Remove</button>
            </div>
          </>
        )}

        {/* ── ADD FOOD ── */}
        {mode === "add-food" && (
          <>
            <div style={{ ...styles.formLabel, marginBottom: 12 }}>Add food</div>
            <div style={styles.dishBuilder}>
              <div style={styles.dbField}>
                <div style={styles.dbLabel}>Dish name</div>
                <input style={styles.input} value={dbName} onChange={(e) => setDbName(e.target.value)} placeholder="e.g. Truffle tagliatelle" onKeyDown={(e) => e.key === "Enter" && addFood()} autoFocus />
              </div>
              <div style={styles.dbField}>
                <div style={styles.dbLabel}>Rating</div>
                <StarPicker value={dbRating} onChange={setDbRating} />
              </div>
              <div style={styles.dbField}>
                <div style={styles.dbLabel}>Category</div>
                <PillSelector options={CATS} value={dbCat} onChange={(v) => setDbCat(v || "Appetizer")} />
              </div>
              <div style={styles.dbField}>
                <div style={styles.dbLabel}>Photo</div>
                <label style={styles.photoPill}>
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleNewPhoto} />
                  {dbPhotoPreview ? <><img src={dbPhotoPreview} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover" }} /> Photo added</> : "📷 Add photo"}
                </label>
              </div>
              <div style={{ ...styles.dbField, marginBottom: 0 }}>
                <div style={styles.dbLabel}>Comments</div>
                <textarea style={{ ...styles.input, resize: "none", fontSize: 13 }} rows={2} value={dbComment} onChange={(e) => setDbComment(e.target.value)} placeholder="How was it?" />
              </div>
            </div>
            <button style={{ ...styles.addDishBtn, marginTop: 12 }} onClick={addFood}>Add dish</button>
          </>
        )}

        {/* ── ADD BEVERAGE ── */}
        {mode === "add-beverage" && (
          <>
            <div style={{ ...styles.formLabel, marginBottom: 12 }}>Add a beverage</div>
            <div style={styles.dishBuilder}>
              <div style={styles.dbField}>
                <div style={styles.dbLabel}>Beverage type</div>
                <PillSelector options={BEV_TYPES} value={bevType} onChange={setBevType} />
              </div>
              {bevType && (
                <>
                  <div style={styles.dbField}>
                    <div style={styles.dbLabel}>Name</div>
                    <input style={styles.input} value={bevName} onChange={(e) => setBevName(e.target.value)}
                      placeholder={bevType === "Beer" ? "e.g. Hazy IPA" : bevType === "Wine" ? "e.g. Cabernet" : "e.g. Old Fashioned"}
                      onKeyDown={(e) => e.key === "Enter" && addBeverage()} autoFocus />
                  </div>
                  <div style={styles.dbField}>
                    <div style={styles.dbLabel}>Rating</div>
                    <StarPicker value={bevRating} onChange={setBevRating} />
                  </div>
                  {styleOptions.length > 0 && (
                    <div style={styles.dbField}>
                      <div style={styles.dbLabel}>{bevType === "Beer" ? "Beer type" : bevType === "Wine" ? "Wine type" : "Base spirit"}</div>
                      <PillSelector options={styleOptions} value={bevStyle} onChange={setBevStyle} />
                    </div>
                  )}
                  {bevType === "Beer" && (
                    <div style={styles.dbField}>
                      <div style={styles.dbLabel}>ABV % (optional)</div>
                      <input style={{ ...styles.input, width: "50%" }} type="number" min="0" max="100" step="0.1" value={bevAbv} onChange={(e) => setBevAbv(e.target.value)} placeholder="e.g. 6.5" />
                    </div>
                  )}
                  <div style={{ ...styles.dbField, marginBottom: 0 }}>
                    <div style={styles.dbLabel}>Comments</div>
                    <textarea style={{ ...styles.input, resize: "none", fontSize: 13 }} rows={2} value={bevNotes} onChange={(e) => setBevNotes(e.target.value)} placeholder="How was it?" />
                  </div>
                  <button style={styles.addDishBtn} onClick={addBeverage}>Add beverage</button>
                </>
              )}
            </div>
          </>
        )}

        {/* ── DEFAULT: VISIT DETAILS + ITEM LIST ── */}
        {!mode && (
          <>
            <div style={styles.formSection}>
              <div style={styles.formLabel}>Type of venue</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {VENUE_TYPES.map(v => (
                  <span key={v.id} onClick={() => setVenueType(v.id)} style={{
                    padding: "8px 16px", borderRadius: 20, fontSize: 13, fontWeight: 500,
                    cursor: "pointer", userSelect: "none",
                    border: venueType === v.id ? "none" : "0.5px solid var(--color-border-secondary)",
                    background: venueType === v.id ? "#C94F2C" : "var(--color-background-secondary)",
                    color: venueType === v.id ? "#fff" : "var(--color-text-secondary)",
                  }}>{v.emoji} {v.label}</span>
                ))}
              </div>
            </div>

            <div style={styles.formSection}>
              <div style={styles.formLabel}>Restaurant</div>
              <input style={styles.input} value={rest} onChange={(e) => setRest(e.target.value)} placeholder="Restaurant name" />
            </div>

            <div style={styles.formSection}>
              <div style={styles.formLabel}>Date</div>
              <input style={styles.input} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>

            <div style={styles.formSection}>
              <div style={styles.formLabel}>Dining type (optional)</div>
              <div style={{ display: "flex", gap: 8 }}>
                {["Dine In", "Take Out", "Delivery"].map((t) => (
                  <span key={t} onClick={() => setDiningType(diningType === t ? "" : t)} style={{ flex: 1, textAlign: "center", padding: "10px 6px", borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: "pointer", userSelect: "none", border: diningType === t ? "none" : "0.5px solid var(--color-border-secondary)", background: diningType === t ? "#C94F2C" : "var(--color-background-secondary)", color: diningType === t ? "#fff" : "var(--color-text-secondary)" }}>{t}</span>
                ))}
              </div>
            </div>

            <div style={styles.formSection}>
              <div style={styles.formLabel}>Cuisine (optional)</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: cuisine === "Other" ? 8 : 0 }}>
                {CUISINES.map(c => (
                  <span key={c} onClick={() => { setCuisine(cuisine === c ? "" : c); if (c !== "Other") setCuisineOther(""); }} style={{
                    padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                    cursor: "pointer", userSelect: "none",
                    border: cuisine === c ? "none" : "0.5px solid var(--color-border-secondary)",
                    background: cuisine === c ? "#C94F2C" : "var(--color-background-secondary)",
                    color: cuisine === c ? "#fff" : "var(--color-text-secondary)",
                  }}>{c}</span>
                ))}
              </div>
              {cuisine === "Other" && (
                <input style={{ ...styles.input, marginTop: 8 }} value={cuisineOther}
                  onChange={e => setCuisineOther(e.target.value)} placeholder="e.g. Peruvian, Fusion..." />
              )}
            </div>

            <div style={styles.divider} />
            <div style={{ ...styles.formLabel, marginBottom: 10 }}>Items</div>

            {dishes.map((d, i) => (
              <div key={i} style={{ ...styles.loggedDish, marginBottom: 8, cursor: "pointer" }} onClick={() => openEditDish(i)}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 3 }}>{d.name}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    {d.rating > 0 ? <StarDisplay rating={d.rating} size={12} /> : <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>No rating</span>}
                    <span style={styles.catBadge}>{d.cat}</span>
                    {d.beer_type && <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{d.beer_type}</span>}
                    {d.abv && <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{d.abv}% ABV</span>}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: "#C94F2C", fontWeight: 500, marginRight: 4 }}>Edit</span>
                <span style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>›</span>
              </div>
            ))}

            <div style={{ display: "flex", gap: 8, marginTop: 12, marginBottom: 4 }}>
              <button style={{ ...styles.addDishBtn, flex: 1, marginTop: 0 }} onClick={() => setMode("add-food")}>🍽 Add food</button>
              <button style={{ ...styles.addDishBtn, flex: 1, marginTop: 0 }} onClick={() => setMode("add-beverage")}>🥤 Add beverage</button>
            </div>

            <div style={styles.divider} />
            <button style={{ ...styles.saveVisitBtn, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save changes"}
            </button>
            <div style={{ height: 16 }} />
          </>
        )}
      </div>
      <BottomNav active="venues" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}

// ── WAITING LIST SCREEN ───────────────────────────────────

// ── RESTAURANT DETAIL SCREEN ──────────────────────────────
function RestaurantDetailScreen({ visits, friendVisits, restKey, onBack, onOpenDish, onNavigate, friendRequestCount, user, cachedFriendships }) {
  const [loadedFriendVisits, setLoadedFriendVisits] = useState(friendVisits || []);
  const [showAllSurvey, setShowAllSurvey] = useState(false);

  useEffect(() => {
    if (!user) return;
    async function loadFriends() {
      try {
        const allFriendships = cachedFriendships?.length ? cachedFriendships
          : (await supabase.from("friendships").select("id, requester_id, addressee_id, status")).data || [];
        const friendIds = getFriendIds(allFriendships, user.id);
        if (!friendIds.length) return;
        const { data: profiles } = await supabase.from("profiles").select(PROFILE_FIELDS).in("id", friendIds);
        const profileMap = {};
        (profiles || []).forEach(p => profileMap[p.id] = p);
        const { data: fVisits } = await supabase.from("visits").select(VISIT_FIELDS).in("user_id", friendIds);
        const { data: fDishes } = await supabase.from("dishes")
          .select("id, visit_id, name, rating, cat, comment, beer_type, abv")
          .in("visit_id", (fVisits || []).map(v => v.id));
        setLoadedFriendVisits((fVisits || []).map(v => ({
          ...v,
          dishes: (fDishes || []).filter(d => d.visit_id === v.id),
          friendName: getDisplayName(profileMap[v.user_id]), friendAvatar: profileMap[v.user_id]?.avatar_url || null,
          isFriend: true,
        })));
      } catch (err) { console.error(err); }
    }
    loadFriends();
  }, [user]);

  const allVisits = [...(visits || []), ...loadedFriendVisits];
  const restVisits = allVisits.filter((v) => (v.place_id || v.rest) === restKey);
  if (!restVisits.length) return null;

  const restName = restVisits[0].rest;
  const restAddress = restVisits[0].address || "";
  const allDishes = restVisits.flatMap((v) =>
    v.dishes.map(d => ({ ...d, isFriend: !!v.isFriend, friendName: v.friendName || null }))
  );
  const uniqueDishes = getUniqueDishes(allDishes);
  const avg = avgRating(allDishes);

  const myVisits = restVisits.filter(v => !v.isFriend);
  const fVisits = restVisits.filter(v => v.isFriend);

  // Aggregate survey data
  function aggregateSurvey(visitsToAggregate) {
    const result = { would_return: {}, wait_time: {}, service_ratings: [], occasion: {}, would_recommend: {} };
    visitsToAggregate.forEach(v => {
      if (v.would_return) result.would_return[v.would_return] = (result.would_return[v.would_return] || 0) + 1;
      if (v.wait_time) result.wait_time[v.wait_time] = (result.wait_time[v.wait_time] || 0) + 1;
      if (v.service_rating) result.service_ratings.push(v.service_rating);
      if (v.occasion) result.occasion[v.occasion] = (result.occasion[v.occasion] || 0) + 1;
      if (v.would_recommend) result.would_recommend[v.would_recommend] = (result.would_recommend[v.would_recommend] || 0) + 1;
    });
    return result;
  }

  function topEntry(obj) {
    const entries = Object.entries(obj);
    if (!entries.length) return null;
    return entries.sort((a, b) => b[1] - a[1])[0];
  }

  const friendRestVisits = fVisits.filter(v => v.survey_completed_at || v.would_return);
  const myRestVisits = myVisits.filter(v => v.survey_completed_at || v.would_return);
  const surveySource = showAllSurvey ? [...myRestVisits, ...friendRestVisits] : friendRestVisits.length ? friendRestVisits : myRestVisits;
  const hasSurveyData = surveySource.length > 0;
  const agg = hasSurveyData ? aggregateSurvey(surveySource) : null;
  const avgService = agg?.service_ratings.length ? (agg.service_ratings.reduce((a,b) => a+b, 0) / agg.service_ratings.length).toFixed(1) : null;

  const byCat = {};
  CATS.forEach((c) => {
    const items = uniqueDishes.filter((d) => d.cat === c);
    if (items.length) byCat[c] = items;
  });

  return (
    <div style={styles.screen}>
      <TopBar subtitle={restName} left={<button style={styles.backBtn} onClick={onBack}>‹ Venues</button>} />
      <div style={styles.scrollBody}>
        <div style={styles.detailHeader}>
          <div style={styles.detailRest}>{restName}</div>
          {restAddress && <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 4 }}>{restAddress}</div>}
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 6 }}>
            {myVisits.length > 0 && `${myVisits.length} your visit${myVisits.length !== 1 ? "s" : ""}`}
            {myVisits.length > 0 && fVisits.length > 0 && " · "}
            {fVisits.length > 0 && `${fVisits.length} friend visit${fVisits.length !== 1 ? "s" : ""}`}
          </div>
          {avg > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <StarDisplay rating={Math.round(avg)} size={15} />
              <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                {avg.toFixed(1)} overall avg · {uniqueDishes.length} unique dish{uniqueDishes.length !== 1 ? "es" : ""}
              </span>
            </div>
          )}

          {/* Aggregated survey data */}
          {hasSurveyData && agg && (
            <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 12, background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-secondary)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-primary)" }}>
                  {showAllSurvey ? "All visitors say" : friendRestVisits.length ? "Friends say" : "You say"}
                </div>
                {(friendRestVisits.length > 0 && myRestVisits.length > 0) && (
                  <button onClick={() => setShowAllSurvey(!showAllSurvey)}
                    style={{ fontSize: 11, color: "#C94F2C", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>
                    {showAllSurvey ? "Friends only" : "See all ›"}
                  </button>
                )}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {topEntry(agg.would_return) && (
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: topEntry(agg.would_return)[0] === "Yes" ? "#EAF4EA" : topEntry(agg.would_return)[0] === "No" ? "#FFF0F0" : "#FFF5F2", color: topEntry(agg.would_return)[0] === "Yes" ? "#3B6D11" : topEntry(agg.would_return)[0] === "No" ? "#C94F2C" : "#9A5C00", fontWeight: 500 }}>
                    🔄 Return: {Object.entries(agg.would_return).map(([k,v]) => `${v} ${k}`).join(", ")}
                  </span>
                )}
                {topEntry(agg.would_recommend) && (
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: topEntry(agg.would_recommend)[0] === "Yes" ? "#EAF4EA" : topEntry(agg.would_recommend)[0] === "No" ? "#FFF0F0" : "#FFF5F2", color: topEntry(agg.would_recommend)[0] === "Yes" ? "#3B6D11" : topEntry(agg.would_recommend)[0] === "No" ? "#C94F2C" : "#9A5C00", fontWeight: 500 }}>
                    👍 Recommend: {Object.entries(agg.would_recommend).map(([k,v]) => `${v} ${k}`).join(", ")}
                  </span>
                )}
                {topEntry(agg.wait_time) && (
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", fontWeight: 500, color: "var(--color-text-secondary)" }}>
                    ⏱ Wait: {topEntry(agg.wait_time)[0]}
                  </span>
                )}
                {avgService && (
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", fontWeight: 500, color: "var(--color-text-secondary)" }}>
                    ⭐ Service: {avgService}
                  </span>
                )}
                {topEntry(agg.occasion) && (
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", fontWeight: 500, color: "var(--color-text-secondary)" }}>
                    🎉 {topEntry(agg.occasion)[0]}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Dishes grouped by category */}
        {Object.entries(byCat).map(([cat, dishes]) => (
          <div key={cat} style={{ marginBottom: 16 }}>
            <div style={styles.catGroupLabel}><span style={styles.catDot} />{cat}</div>
            {dishes.map((d, i) => (
              <div key={i} style={{ ...styles.detailDish, cursor: "pointer" }}
                onClick={() => onOpenDish({ name: d.name, cat: d.cat, restKey, restName })}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>{d.name}</div>
                  <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>›</span>
                </div>
                {(d.hasMyRating || d.hasFriendRating) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                    {d.hasMyRating && <span style={{ fontSize: 11, background: "#FAECE7", color: "#C94F2C", borderRadius: 20, padding: "2px 8px", fontWeight: 500 }}>You</span>}
                    {d.hasFriendRating && d.friendNames.map(n => (
                      <span key={n} style={{ fontSize: 11, background: "#EAF4EA", color: "#3B6D11", borderRadius: 20, padding: "2px 8px", fontWeight: 500 }}>{n}</span>
                    ))}
                  </div>
                )}
                {d.avgRating > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                    <StarDisplay rating={Math.round(d.avgRating)} size={13} />
                    <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                      {d.avgRating.toFixed(1)}{d.count > 1 ? ` · ordered ${d.count}x` : ""}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Friends' visits section */}
        {fVisits.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={styles.catGroupLabel}><span style={styles.catDot} />Friends' visits</div>
            {fVisits.map((v, i) => {
              const ds = new Date(v.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
              const vAvg = avgRating(v.dishes);
              return (
                <div key={i} style={{ ...styles.detailDish }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#C94F2C" }}>{v.friendName}</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{ds}</div>
                  </div>
                  {vAvg > 0 && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><StarDisplay rating={Math.round(vAvg)} size={12} /><span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{vAvg.toFixed(1)}</span></div>}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {v.dishes.map((d, j) => (
                      <span key={j} style={styles.chip}>{d.name}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ height: 16 }} />
      </div>
      <BottomNav active="venues" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}

// ── DISH REVIEWS SCREEN ───────────────────────────────────
function DishReviewsScreen({ visits, friendVisits, dishInfo, onBack, onNavigate, friendRequestCount, user }) {
  const [loadedFriendVisits, setLoadedFriendVisits] = useState(friendVisits || []);

  useEffect(() => {
    if (!user) return;
    async function load() {
      try {
        const { data: friendships } = await supabase.from("friendships").select("id, requester_id, addressee_id, status");
        const accepted = (friendships || []).filter(f => f.status === "accepted");
        const friendIds = accepted.map(f => f.requester_id === user.id ? f.addressee_id : f.requester_id);
        if (!friendIds.length) return;
        const { data: profiles } = await supabase.from("profiles").select("id, email, username, avatar_url").in("id", friendIds);
        const profileMap = {};
        (profiles || []).forEach(p => profileMap[p.id] = p);
        const { data: fVisits } = await supabase.from("visits").select(VISIT_FIELDS).in("user_id", friendIds);
        const { data: fDishes } = await supabase.from("dishes").select(DISH_FIELDS).in("visit_id", (fVisits || []).map(v => v.id));
        setLoadedFriendVisits((fVisits || []).map(v => ({
          ...v,
          dishes: (fDishes || []).filter(d => d.visit_id === v.id),
          friendName: getDisplayName(profileMap[v.user_id]), friendAvatar: profileMap[v.user_id]?.avatar_url || null,
          isFriend: true,
        })));
      } catch (err) { console.error(err); }
    }
    load();
  }, [user]);

  const { name, restKey, restName } = dishInfo;
  const allVisits = [...(visits || []), ...loadedFriendVisits];
  const restVisits = allVisits.filter((v) => (v.place_id || v.rest) === restKey);
  const allReviews = restVisits
    .flatMap((v) => v.dishes
      .filter((d) => d.name.toLowerCase() === name.toLowerCase())
      .map((d) => ({ ...d, visitDate: v.date, visitId: v.id, reviewerName: v.isFriend ? v.friendName : "You", isFriend: !!v.isFriend }))
    )
    .sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate));

  const avg = avgRating(allReviews);

  return (
    <div style={styles.screen}>
      <TopBar subtitle={name} left={<button style={styles.backBtn} onClick={onBack}>‹ {restName}</button>} />
      <div style={styles.scrollBody}>
        <div style={styles.detailHeader}>
          <div style={styles.detailRest}>{name}</div>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 6 }}>
            {restName} · {allReviews.length} review{allReviews.length !== 1 ? "s" : ""}
          </div>
          {avg > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <StarDisplay rating={Math.round(avg)} size={15} />
              <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{avg.toFixed(1)} avg</span>
            </div>
          )}
        </div>

        <div style={{ ...styles.formLabel, marginBottom: 10 }}>All reviews — newest first</div>

        {allReviews.map((r, i) => {
          const ds = new Date(r.visitDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
          return (
            <div key={i} style={styles.detailDish}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{
                  fontSize: 12, fontWeight: 600,
                  color: r.isFriend ? "#3B6D11" : "#C94F2C",
                  background: r.isFriend ? "#EAF4EA" : "#FAECE7",
                  borderRadius: 20, padding: "2px 10px",
                }}>{r.reviewerName}</span>
                <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{ds}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", marginBottom: r.comment || r.photo ? 4 : 0 }}>
                {r.rating > 0
                  ? <><StarDisplay rating={r.rating} size={14} /><span style={{ fontSize: 12, color: "var(--color-text-secondary)", marginLeft: 6 }}>{LABELS[r.rating]}</span></>
                  : <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>No rating</span>
                }
              </div>
              {r.photo && <img src={photoSrc(r.photo)} alt={name} style={{ width: "100%", borderRadius: 8, marginBottom: r.comment ? 6 : 0, maxHeight: 180, objectFit: "cover" }} />}
              {r.comment && <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>{r.comment}</div>}
            </div>
          );
        })}
        <div style={{ height: 16 }} />
      </div>
      <BottomNav active="venues" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}

// ── helper: merge same-named dishes and average ratings ───
function getUniqueDishes(dishes) {
  const map = {};
  dishes.forEach((d) => {
    const key = d.name.toLowerCase();
    if (!map[key]) {
      map[key] = { name: d.name, cat: d.cat, ratings: [], count: 0, hasMyRating: false, hasFriendRating: false, friendNames: [] };
    }
    map[key].count++;
    if (d.rating > 0) map[key].ratings.push(d.rating);
    if (d.isFriend) {
      map[key].hasFriendRating = true;
      if (d.friendName && !map[key].friendNames.includes(d.friendName)) map[key].friendNames.push(d.friendName);
    } else {
      map[key].hasMyRating = true;
    }
  });
  return Object.values(map).map((d) => ({
    ...d,
    avgRating: d.ratings.length ? d.ratings.reduce((s, r) => s + r, 0) / d.ratings.length : 0,
  }));
}

// ── FRIEND SETTINGS SCREEN ───────────────────────────────
function FriendSettingsScreen({ user, onBack, onFriendsChanged, prefillEmail = "", onViewFriend, onNavigate, friendRequestCount }) {
  const [friends, setFriends] = useState([]);
  const [pending, setPending] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [searchEmail, setSearchEmail] = useState(prefillEmail);
  const [searchResult, setSearchResult] = useState(null);
  const [searchStatus, setSearchStatus] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  // Auto-search if opened with a prefill email
  useEffect(() => {
    if (prefillEmail) searchForUser(prefillEmail);
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("friendships").select("id, requester_id, addressee_id, status");
      if (error) throw error;

      const accepted = data.filter(f => f.status === "accepted");
      const sentPending = data.filter(f => f.status === "pending" && f.requester_id === user.id);
      const receivedPending = data.filter(f => f.status === "pending" && f.addressee_id === user.id);

      const allIds = [
        ...accepted.map(f => f.requester_id === user.id ? f.addressee_id : f.requester_id),
        ...sentPending.map(f => f.addressee_id),
        ...receivedPending.map(f => f.requester_id),
      ];

      if (allIds.length > 0) {
        const { data: profiles } = await supabase.from("profiles").select("id, email, username, avatar_url").in("id", allIds);
        const profileMap = {};
        (profiles || []).forEach(p => profileMap[p.id] = p);

        setFriends(accepted.map(f => ({
          ...f,
          friendId: f.requester_id === user.id ? f.addressee_id : f.requester_id,
          email: profileMap[f.requester_id === user.id ? f.addressee_id : f.requester_id]?.email || "Unknown",
        })));
        setPending(sentPending.map(f => ({ ...f, email: profileMap[f.addressee_id]?.email || "Unknown" })));
        setIncoming(receivedPending.map(f => ({ ...f, email: profileMap[f.requester_id]?.email || "Unknown" })));
      } else {
        setFriends([]); setPending([]); setIncoming([]);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function searchForUser(emailOverride) {
    const email = (typeof emailOverride === "string" ? emailOverride : searchEmail).trim();
    if (!email) { setSearchStatus("Please enter an email address."); return; }
    setSearchResult(null);
    setSearchStatus("Searching...");
    try {
      const { data, error } = await supabase.from("profiles").select("id, email, username, avatar_url").eq("email", email.toLowerCase()).maybeSingle();
      if (error) { setSearchStatus("Search failed: " + error.message); return; }
      if (!data) { setSearchStatus("No user found with that email."); return; }
      if (data.id === user.id) { setSearchStatus("That's you!"); return; }

      const { data: existing } = await supabase.from("friendships").select("id, requester_id, addressee_id, status")
        .or(`and(requester_id.eq.${user.id},addressee_id.eq.${data.id}),and(requester_id.eq.${data.id},addressee_id.eq.${user.id})`)
        .maybeSingle();

      if (existing?.status === "accepted") { setSearchStatus("You are already friends!"); return; }
      if (existing?.status === "pending") { setSearchStatus("Friend request already sent."); return; }

      setSearchResult(data);
      setSearchStatus("");
    } catch (err) { setSearchStatus("Error: " + err.message); }
  }

  async function sendRequest(addresseeId) {
    try {
      const { error } = await supabase.from("friendships").insert({ requester_id: user.id, addressee_id: addresseeId, status: "pending" });
      if (error) throw error;
      setSearchResult(null); setSearchEmail(""); setSearchStatus("Friend request sent!");
      loadAll(); onFriendsChanged();
    } catch (err) { alert("Error: " + err.message); }
  }

  async function respondToRequest(friendshipId, accept) {
    try {
      if (accept) {
        await supabase.from("friendships").update({ status: "accepted" }).eq("id", friendshipId);
      } else {
        await supabase.from("friendships").delete().eq("id", friendshipId);
      }
      loadAll(); onFriendsChanged();
    } catch (err) { alert("Error: " + err.message); }
  }

  async function cancelRequest(friendshipId) {
    try {
      await supabase.from("friendships").delete().eq("id", friendshipId);
      loadAll(); onFriendsChanged();
    } catch (err) { alert("Error: " + err.message); }
  }

  async function unfriend(friendshipId) {
    if (!window.confirm("Remove this friend? They won't be notified.")) return;
    try {
      await supabase.from("friendships").delete().eq("id", friendshipId);
      loadAll(); onFriendsChanged();
    } catch (err) { alert("Error: " + err.message); }
  }

  return (
    <div style={styles.screen}>
      <TopBar subtitle="Friends" left={<button style={styles.backBtn} onClick={onBack}>‹ Friends</button>} />
      <div style={styles.scrollBody}>

        {/* Search */}
        <div style={styles.formSection}>
          <div style={styles.formLabel}>Add a friend</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ ...styles.input, flex: 1 }}
              type="email"
              value={searchEmail}
              onChange={(e) => { setSearchEmail(e.target.value); setSearchResult(null); setSearchStatus(""); }}
              placeholder="Enter their email address"
              onKeyDown={(e) => e.key === "Enter" && searchForUser()}
            />
            <button style={{ ...styles.btnPrimary, flexShrink: 0 }} onClick={searchForUser}>Search</button>
          </div>
          {searchStatus && <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 8 }}>{searchStatus}</div>}
          {searchResult && (
            <div style={{ ...styles.loggedDish, marginTop: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>{searchResult.email}</div>
                <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>Rate Your Plate user</div>
              </div>
              <button style={{ ...styles.btnPrimary, flexShrink: 0 }} onClick={() => sendRequest(searchResult.id)}>Add</button>
            </div>
          )}
        </div>

        {/* Incoming requests */}
        {incoming.length > 0 && (
          <div style={styles.formSection}>
            <div style={styles.formLabel}>Friend requests ({incoming.length})</div>
            {incoming.map((f) => (
              <div key={f.id} style={{ ...styles.loggedDish, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>{f.email}</div>
                  <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>wants to be friends</div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={styles.btnPrimary} onClick={() => respondToRequest(f.id, true)}>Accept</button>
                  <button style={styles.actionBtnRed} onClick={() => respondToRequest(f.id, false)}>Decline</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sent pending */}
        {pending.length > 0 && (
          <div style={styles.formSection}>
            <div style={styles.formLabel}>Sent requests ({pending.length})</div>
            {pending.map((f) => (
              <div key={f.id} style={{ ...styles.loggedDish, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>{f.email}</div>
                  <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>Pending</div>
                </div>
                <button style={styles.actionBtnRed} onClick={() => cancelRequest(f.id)}>Cancel</button>
              </div>
            ))}
          </div>
        )}

        {/* Friends with unfriend */}
        <div style={styles.formSection}>
          <div style={styles.formLabel}>Your friends {friends.length > 0 ? `(${friends.length})` : ""}</div>
          {loading && <Spinner />}
          {!loading && friends.length === 0 && (
            <div style={{ fontSize: 14, color: "var(--color-text-tertiary)", padding: "20px 0", textAlign: "center" }}>
              No friends yet.
            </div>
          )}
          {friends.map((f) => (
            <div key={f.id} style={{ ...styles.loggedDish, marginBottom: 8 }}>
              <div style={{ flex: 1, cursor: "pointer" }} onClick={() => onViewFriend && onViewFriend(f.friendId, f.email)}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>{f.email}</div>
                <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>Tap to see visits →</div>
              </div>
              <button style={styles.actionBtnRed} onClick={() => unfriend(f.id)}>Unfriend</button>
            </div>
          ))}
        </div>
        <div style={{ height: 16 }} />
      </div>
      <BottomNav active="friends" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}

// ── FRIENDS SCREEN (Activity Feed) ───────────────────────
function FriendsScreen({ user, onViewFriend, onNavigate, friendRequestCount }) {
  const [activeTab, setActiveTab] = useState("guests");

  // Activity tab
  const [feed, setFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState(null);

  // Guest List tab
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [guestLoading, setGuestLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  // New friend tracking for this session
  const [newFriendIds, setNewFriendIds] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("ryp_new_friends") || "[]"); } catch { return []; }
  });
  const [seenRequests, setSeenRequests] = useState(false);

  // Invite sub-screen
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [copied, setCopied] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [usernameResults, setUsernameResults] = useState([]);

  useEffect(() => { loadFeed(); loadGuests(); }, []);
  useEffect(() => {
    if (showInvite && !inviteCode) generateInviteCode();
  }, [showInvite]);

  // Mark requests as seen after first view
  useEffect(() => {
    if (incoming.length > 0 && !seenRequests) {
      setSeenRequests(true);
    }
  }, [incoming]);

  async function generateInviteCode() {
    const { data: existing } = await supabase.from("invite_codes").select("code").eq("user_id", user.id).maybeSingle();
    if (existing) { setInviteCode(existing.code); return; }
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    await supabase.from("invite_codes").insert({ code, user_id: user.id });
    setInviteCode(code);
  }

  // Debounced search as user types
  useEffect(() => {
    const timer = setTimeout(() => { if (activeTab === "guests") handleSearch(searchQuery); }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  async function loadFeed() {
    setFeedLoading(true);
    try {
      const { data: friendships } = await supabase.from("friendships").select("id, requester_id, addressee_id, status");
      const accepted = (friendships || []).filter(f => f.status === "accepted");
      const friendIds = accepted.map(f => f.requester_id === user.id ? f.addressee_id : f.requester_id);
      if (!friendIds.length) { setFeed([]); setFeedLoading(false); return; }
      const { data: profilesData } = await supabase.from("profiles").select("id, email, username, avatar_url").in("id", friendIds);
      const profileMap = {};
      (profilesData || []).forEach(p => profileMap[p.id] = p);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 7);
      const { data: visitsData } = await supabase.from("visits").select(VISIT_FIELDS)
        .in("user_id", friendIds).gte("date", cutoff.toISOString().split("T")[0]).order("date", { ascending: false });
      if (!visitsData?.length) { setFeed([]); setFeedLoading(false); return; }
      const { data: dishesData } = await supabase.from("dishes")
        .select("id, visit_id, name, rating, cat, comment, photo").in("visit_id", visitsData.map(v => v.id));
      setFeed(visitsData.map(v => ({
        ...v,
        dishes: (dishesData || []).filter(d => d.visit_id === v.id),
        friendEmail: profileMap[v.user_id]?.email || "Unknown",
        friendName: getDisplayName(profileMap[v.user_id]), friendAvatar: profileMap[v.user_id]?.avatar_url || null,
      })));
    } catch (err) { console.error(err); }
    finally { setFeedLoading(false); }
  }

  async function loadGuests() {
    setGuestLoading(true);
    try {
      const { data: friendships, error } = await supabase.from("friendships").select("id, requester_id, addressee_id, status")
        .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`);
      const accepted = (friendships || []).filter(f => f.status === "accepted");
      const inc = (friendships || []).filter(f => f.status === "pending" && f.addressee_id === user.id);
      const outgoing = (friendships || []).filter(f => f.status === "pending" && f.requester_id === user.id);
      const allIds = [...new Set([
        ...accepted.map(f => f.requester_id === user.id ? f.addressee_id : f.requester_id),
        ...inc.map(f => f.requester_id),
        ...outgoing.map(f => f.addressee_id),
      ])];
      const profileMap = {};
      if (allIds.length) {
        const { data: p } = await supabase.from("profiles").select("id, email, username, avatar_url").in("id", allIds);
        (p || []).forEach(x => profileMap[x.id] = x);
      }

      // Detect newly accepted: friends where I was the requester and they just accepted
      // Compare against previous friend list stored in sessionStorage
      const prevFriendIds = JSON.parse(sessionStorage.getItem("ryp_prev_friends") || "[]");
      const currentFriendIds = accepted.map(f => f.requester_id === user.id ? f.addressee_id : f.requester_id);
      const justAccepted = currentFriendIds.filter(id => !prevFriendIds.includes(id) && prevFriendIds.length > 0);
      if (justAccepted.length) {
        const updated = [...new Set([...newFriendIds, ...justAccepted])];
        setNewFriendIds(updated);
        sessionStorage.setItem("ryp_new_friends", JSON.stringify(updated));
      }
      sessionStorage.setItem("ryp_prev_friends", JSON.stringify(currentFriendIds));

      setFriends(accepted);
      setIncoming(inc);
      setOutgoing(outgoing);
      setProfiles(profileMap);
    } catch (err) { console.error(err); }
    finally { setGuestLoading(false); }
  }

  async function handleSearch(q) {
    const query = q.trim().toLowerCase();
    if (!query) { setSearchResult(null); setUsernameResults([]); return; }

    setSearching(true);
    try {
      const isEmail = query.includes("@");

      if (isEmail) {
        // Email: exact match only (privacy)
        const { data } = await supabase.from("profiles").select("id, email, username, avatar_url").eq("email", query).maybeSingle();
        setUsernameResults([]);
        if (data && data.id === user.id) { setSearchResult({ type: "self" }); return; }
        if (data) {
          const { data: fs } = await supabase.from("friendships").select("id, requester_id, addressee_id, status")
            .or(`and(requester_id.eq.${user.id},addressee_id.eq.${data.id}),and(requester_id.eq.${data.id},addressee_id.eq.${user.id})`)
            .maybeSingle();
          if (fs?.status === "accepted") { setSearchResult({ type: "already_friends", data }); return; }
          if (fs?.status === "pending") { setSearchResult({ type: "pending", data }); return; }
          setSearchResult({ type: "user", data });
        } else {
          setSearchResult({ type: "invite", email: query });
        }
      } else {
        // Username: partial match after 2+ chars
        setSearchResult(null);
        if (query.length < 2) { setUsernameResults([]); return; }
        const { data: matches } = await supabase.from("profiles")
          .select("id, username, email")
          .ilike("username", `${query}%`)
          .neq("id", user.id)
          .limit(5);
        if (!matches?.length) { setUsernameResults([]); return; }
        // Tag with friendship status
        const results = await Promise.all((matches || []).map(async (p) => {
          const { data: fs } = await supabase.from("friendships").select("id, requester_id, addressee_id, status")
            .or(`and(requester_id.eq.${user.id},addressee_id.eq.${p.id}),and(requester_id.eq.${p.id},addressee_id.eq.${user.id})`)
            .maybeSingle();
          return { ...p, friendStatus: fs?.status || null };
        }));
        setUsernameResults(results);
      }
    } catch (err) { console.error(err); }
    finally { setSearching(false); }
  }

  async function sendRequest(addresseeId) {
    try {
      await supabase.from("friendships").insert({ requester_id: user.id, addressee_id: addresseeId, status: "pending" });

      // Send email notification
      try {
        const { data: addresseeProfile } = await supabase.from("profiles").select("email").eq("id", addresseeId).single();
        const senderName = getDisplayName(user);
        if (addresseeProfile?.email) {
          // TODO: replace with Supabase Edge Function + Resend for real email delivery
          // For now, call Supabase function if available
          await supabase.functions.invoke("send-friend-request-email", {
            body: {
              to: addresseeProfile.email,
              senderName,
              senderEmail: user.email,
            }
          }).catch(() => {
            // Edge function not deployed yet — log only
            console.log(`Friend request email: to=${addresseeProfile.email}, from=${senderName}`);
          });
        }
      } catch (e) { console.log("Email notification skipped:", e.message); }

      setSearchQuery(""); setSearchResult(null);
      loadGuests();
    } catch (err) { alert("Error: " + err.message); }
  }

  async function respondToRequest(friendshipId, accept) {
    try {
      if (accept) await supabase.from("friendships").update({ status: "accepted" }).eq("id", friendshipId);
      else await supabase.from("friendships").delete().eq("id", friendshipId);
      loadGuests();
    } catch (err) { alert("Error: " + err.message); }
  }

  async function unfriend(friendshipId) {
    if (!window.confirm("Remove this friend?")) return;
    try {
      await supabase.from("friendships").delete().eq("id", friendshipId);
      loadGuests();
    } catch (err) { alert("Error: " + err.message); }
  }

  function getFriendId(f) { return f.requester_id === user.id ? f.addressee_id : f.requester_id; }

  function openInvite(email) {
    setInviteEmail(email);
    setShowInvite(true);
  }

  function handleSendEmail() {
    const subject = encodeURIComponent("You should try Rate Your Plate");
    const body = encodeURIComponent(`Hey! I've been using this app called Rate Your Plate to track and share restaurants, dishes, and drinks with friends. You should check it out!\n\nhttps://rateyourplateapp.com?join=${inviteCode}`);
    window.open(`mailto:${inviteEmail}?subject=${subject}&body=${body}`);
  }

  function handleCopy() {
    const text = `Hey! I've been using this app called Rate Your Plate to track and share restaurants, dishes, and drinks with friends. You should check it out!\n\nhttps://rateyourplateapp.com?join=${inviteCode}`;
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  function getBestPhoto(dishes) {
    const withPhotos = (dishes || []).filter(d => d.photo);
    if (!withPhotos.length) return null;
    const pool = withPhotos.filter(d => d.cat === "Main").length ? withPhotos.filter(d => d.cat === "Main") : withPhotos;
    return [...pool].sort((a, b) => (b.rating || 0) - (a.rating || 0))[0].photo;
  }

  // Filtered friends list based on search query
  const filteredFriends = searchQuery.trim()
    ? friends.filter(f => {
        const p = profiles[getFriendId(f)];
        const q = searchQuery.toLowerCase();
        return p?.username?.toLowerCase().includes(q) || p?.email?.toLowerCase().includes(q);
      })
    : friends;

  // ── INVITE SUB-SCREEN ──
  if (showInvite) {
    return (
      <div style={styles.screen}>
        <TopBar subtitle="Invite a friend" left={<button style={styles.backBtn} onClick={() => setShowInvite(false)}>‹ Friends</button>} />
        <div style={styles.scrollBody}>

          {/* Invite code */}
          <div style={{ textAlign: "center", marginBottom: 24, padding: "20px", background: "var(--color-background-secondary)", borderRadius: 16, border: "0.5px solid var(--color-border-secondary)" }}>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 500 }}>Your invite code</div>
            <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: 6, color: "#C94F2C", fontFamily: "monospace" }}>
              {inviteCode || "..."}
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 8 }}>
              Share this with friends — they enter it when signing up
            </div>
          </div>

          {inviteEmail && (
            <div style={{ marginBottom: 16, padding: "10px 14px", background: "var(--color-background-secondary)", borderRadius: 10, border: "0.5px solid var(--color-border-secondary)" }}>
              <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginBottom: 2 }}>Sending to</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>{inviteEmail}</div>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button style={{ ...styles.pillBtn, flex: 1 }} onClick={handleSendEmail}>📧 Open in email</button>
            <button style={{ ...styles.pillBtn, flex: 1, background: copied ? "#3B6D11" : "#C94F2C" }} onClick={handleCopy}>
              {copied ? "✓ Copied!" : "📋 Copy"}
            </button>
          </div>
        </div>
        <BottomNav active="friends" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
      </div>
    );
  }

  // ── VISIT DETAIL ──
  if (selectedVisit) {
    const avg = avgRating(selectedVisit.dishes);
    const ds = new Date(selectedVisit.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
    const byCat = {};
    CATS.forEach(c => { const items = selectedVisit.dishes.filter(d => d.cat === c); if (items.length) byCat[c] = items; });
    return (
      <div style={styles.screen}>
        <TopBar subtitle={selectedVisit.rest} left={<button style={styles.backBtn} onClick={() => setSelectedVisit(null)}>‹ Activity</button>} />
        <div style={styles.scrollBody}>
          <div style={styles.detailHeader}>
            <div style={styles.detailRest}>{selectedVisit.rest}</div>
            {selectedVisit.address && <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 4 }}>{selectedVisit.address}</div>}
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 6 }}>{ds}</div>
            <div style={{ fontSize: 13, color: "#C94F2C", fontWeight: 500, cursor: "pointer", marginBottom: 6 }}
              onClick={() => { setSelectedVisit(null); onViewFriend(selectedVisit.user_id, selectedVisit.friendEmail); }}>
              {selectedVisit.friendName}'s visits →
            </div>
            {avg > 0 && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><StarDisplay rating={Math.round(avg)} size={15} /><span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{avg.toFixed(1)} avg · {selectedVisit.dishes.length} items</span></div>}
          </div>
          {Object.entries(byCat).map(([cat, dishes]) => (
            <div key={cat} style={{ marginBottom: 16 }}>
              <div style={styles.catGroupLabel}><span style={styles.catDot} />{cat}</div>
              {dishes.map((d, i) => (
                <div key={i} style={styles.detailDish}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 4 }}>{d.name}</div>
                  {d.rating > 0 && <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: d.comment ? 4 : 0 }}><StarDisplay rating={d.rating} size={14} /><span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{LABELS[d.rating]}</span></div>}
                  {d.comment && <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>{d.comment}</div>}
                  {photoSrc(d.photo) && <img src={photoSrc(d.photo)} alt={d.name} style={{ width: "100%", borderRadius: 8, marginTop: 8, maxHeight: 180, objectFit: "cover" }} />}
                </div>
              ))}
            </div>
          ))}
        </div>
        <BottomNav active="friends" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
      </div>
    );
  }

  // ── MAIN SCREEN ──
  return (
    <div style={styles.screen}>
      <TopBar subtitle="Friends" />

      <div style={styles.scrollBody}>

        {/* ── GUEST LIST ── */}
        {activeTab === "guests" && (
          <>
            {/* Smart search bar */}
            <div style={{ marginBottom: 16 }}>
              <input
                style={{ ...styles.input, fontSize: 14 }}
                placeholder="Search by username or enter email..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setUsernameResults([]); }}
                autoCapitalize="none"
                autoCorrect="off"
              />

              {/* Username autocomplete results */}
              {!searching && usernameResults.length > 0 && (
                <div style={{ marginTop: 4, borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "var(--color-background-primary)", overflow: "hidden" }}>
                  {usernameResults.map((p, i) => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: i < usernameResults.length - 1 ? "0.5px solid var(--color-border-tertiary)" : "none" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>@{p.username}</div>
                      </div>
                      {p.friendStatus === "accepted" ? (
                        <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>Friends</span>
                      ) : p.friendStatus === "pending" ? (
                        <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>Pending</span>
                      ) : (
                        <button style={styles.pillBtn} onClick={() => { sendRequest(p.id); setSearchQuery(""); setUsernameResults([]); }}>Add</button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Search result */}
              {searching && <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", marginTop: 8, paddingLeft: 4 }}>Searching...</div>}

              {!searching && searchResult && (
                <div style={{ marginTop: 8, padding: "12px 14px", borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "var(--color-background-primary)" }}>
                  {searchResult.type === "user" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 2 }}>{getDisplayName(searchResult.data)}</div>
                        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{searchResult.data.email}</div>
                      </div>
                      <button style={styles.pillBtn} onClick={() => sendRequest(searchResult.data.id)}>Add friend</button>
                    </div>
                  )}
                  {searchResult.type === "invite" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 2 }}>Not on Rate Your Plate yet</div>
                        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{searchResult.email}</div>
                      </div>
                      <button style={styles.pillBtn} onClick={() => openInvite(searchResult.email)}>Invite</button>
                    </div>
                  )}
                  {searchResult.type === "already_friends" && (
                    <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>✓ Already friends with {getDisplayName(searchResult.data)}</div>
                  )}
                  {searchResult.type === "pending" && (
                    <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>⏳ Request already sent to {getDisplayName(searchResult.data)}</div>
                  )}
                  {searchResult.type === "self" && (
                    <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>That's you!</div>
                  )}
                </div>
              )}
            </div>

            {/* Outgoing pending requests */}
            {outgoing.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ ...styles.formLabel, marginBottom: 10 }}>Pending requests</div>
                {outgoing.map(f => {
                  const p = profiles[f.addressee_id];
                  return (
                    <div key={f.id} style={{ ...styles.card, display: "flex", alignItems: "center", gap: 12, marginBottom: 8, padding: "12px 14px", opacity: 0.8 }}>
                      <Avatar url={p?.avatar_url} fallback={p?.username?.[0]?.toUpperCase() || "?"} size={36} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 1 }}>{getDisplayName(p)}</div>
                        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{p?.email}</div>
                        <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>⏳ Waiting for them to accept</div>
                      </div>
                      <button style={{ ...styles.pillBtnGhost, fontSize: 12, padding: "6px 12px" }}
                        onClick={async () => {
                          await supabase.from("friendships").delete().eq("id", f.id);
                          loadGuests();
                        }}>Cancel</button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Incoming pending requests */}
            {incoming.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <div style={styles.formLabel}>Friend requests</div>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#C94F2C" }} />
                </div>
                {incoming.map(f => {
                  const p = profiles[f.requester_id];
                  return (
                    <div key={f.id} style={{ ...styles.card, display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "12px 14px", border: "1.5px solid #C94F2C" }}>
                      <Avatar url={p?.avatar_url} fallback={p?.username?.[0]?.toUpperCase() || "?"} size={36} border />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 2 }}>{getDisplayName(p)}</div>
                        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{p?.email}</div>
                        <div style={{ fontSize: 11, color: "#C94F2C", marginTop: 2 }}>wants to be friends</div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <button style={{ ...styles.pillBtn, fontSize: 12, padding: "6px 14px" }} onClick={() => respondToRequest(f.id, true)}>Accept</button>
                        <button style={{ ...styles.pillBtnGhost, fontSize: 12, padding: "6px 14px" }} onClick={() => respondToRequest(f.id, false)}>Decline</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Friends list */}
            {guestLoading && <Spinner />}
            {!guestLoading && filteredFriends.length === 0 && !searchQuery && incoming.length === 0 && outgoing.length === 0 && (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 48, marginBottom: 14 }}>👥</div>
                <div style={styles.emptyH}>No friends yet</div>
                <div style={styles.emptyP}>Search for a friend by email above, or invite someone to join.</div>
              </div>
            )}
            {!guestLoading && filteredFriends.length === 0 && searchQuery && !searchResult && (
              <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", textAlign: "center", marginTop: 8 }}>No friends matching "{searchQuery}"</div>
            )}
            {filteredFriends.length > 0 && (() => {
              // Sort new friends to top
              const sorted = [...filteredFriends].sort((a, b) => {
                const aNew = newFriendIds.includes(getFriendId(a));
                const bNew = newFriendIds.includes(getFriendId(b));
                return bNew - aNew;
              });
              return (
                <>
                  <div style={{ ...styles.formLabel, marginBottom: 10 }}>Your friends</div>
                  {sorted.map(f => {
                    const fid = getFriendId(f);
                    const p = profiles[fid];
                    const isNew = newFriendIds.includes(fid);
                    return (
                      <div key={f.id} style={{ ...styles.card, display: "flex", alignItems: "center", gap: 12, marginBottom: 8, padding: "12px 14px", cursor: "pointer", border: isNew ? "1.5px solid #3B6D11" : undefined }}
                        onClick={() => {
                          // Clear new indicator on click
                          if (isNew) {
                            const updated = newFriendIds.filter(id => id !== fid);
                            setNewFriendIds(updated);
                            sessionStorage.setItem("ryp_new_friends", JSON.stringify(updated));
                          }
                          onViewFriend(fid, p?.email || "");
                        }}>
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          <Avatar url={p?.avatar_url} fallback={p?.username?.[0]?.toUpperCase() || "?"} size={36} />
                          {isNew && <div style={{ position: "absolute", top: -1, right: -1, width: 10, height: 10, borderRadius: "50%", background: "#3B6D11", border: "2px solid var(--color-background-primary)" }} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 1 }}>{getDisplayName(p)}</div>
                            {isNew && <span style={{ fontSize: 10, background: "#EAF4EA", color: "#3B6D11", borderRadius: 20, padding: "1px 7px", fontWeight: 600 }}>New!</span>}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{p?.email}</div>
                        </div>
                        <button style={styles.pillBtnGhost} onClick={(e) => { e.stopPropagation(); unfriend(f.id); }}>Unfriend</button>
                      </div>
                    );
                  })}
                </>
              );
            })()}

          </>
        )}

        <div style={{ height: 16 }} />
      </div>
      <BottomNav active="friends" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}

// ── FRIEND VISITS SCREEN ──────────────────────────────────
function FriendVisitsScreen({ friendId, friendEmail, onBack, onNavigate, friendRequestCount }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starFilter, setStarFilter] = useState(0);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [friendUsername, setFriendUsername] = useState(getDisplayName({email: friendEmail}));

  useEffect(() => {
    loadFriendVisits();
    supabase.from("profiles").select("username").eq("id", friendId).single()
      .then(({ data }) => { if (data?.username) setFriendUsername(data.username); });
  }, []);

  async function loadFriendVisits() {
    setLoading(true);
    try {
      const { data: visitsData, error: visitsError } = await supabase
        .from("visits").select(VISIT_FIELDS).eq("user_id", friendId).order("date", { ascending: false });
      if (visitsError) throw visitsError;

      const { data: dishesData, error: dishesError } = await supabase
        .from("dishes").select(DISH_FIELDS).eq("user_id", friendId);
      if (dishesError) throw dishesError;

      const combined = visitsData.map((v) => ({
        ...v,
        dishes: dishesData.filter((d) => d.visit_id === v.id),
      }));
      setVisits(combined);
    } catch (err) {
      alert("Error loading visits: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  // Filter visits where at least one dish matches the star filter
  const filtered = starFilter > 0
    ? visits.filter(v => v.dishes.some(d => d.rating >= starFilter))
    : visits;

  if (selectedVisit) {
    const avg = avgRating(selectedVisit.dishes);
    const ds = new Date(selectedVisit.date + "T12:00:00").toLocaleDateString("en-US", {
      weekday: "short", month: "long", day: "numeric", year: "numeric",
    });
    const byCat = {};
    CATS.forEach((c) => {
      const items = selectedVisit.dishes.filter((d) => d.cat === c);
      if (items.length) byCat[c] = items;
    });
    return (
      <div style={styles.screen}>
        <TopBar subtitle={selectedVisit.rest} left={<button style={styles.backBtn} onClick={() => setSelectedVisit(null)}>‹ {getDisplayName({email: friendEmail})}</button>} />
        <div style={styles.scrollBody}>
          <div style={styles.detailHeader}>
            <div style={styles.detailRest}>{selectedVisit.rest}</div>
            {selectedVisit.address && <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 4 }}>{selectedVisit.address}</div>}
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 6 }}>{ds}</div>
            {avg > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <StarDisplay rating={Math.round(avg)} size={15} />
                <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{avg.toFixed(1)} avg · {selectedVisit.dishes.length} items</span>
              </div>
            )}
          </div>
          {Object.entries(byCat).map(([cat, dishes]) => (
            <div key={cat} style={{ marginBottom: 16 }}>
              <div style={styles.catGroupLabel}><span style={styles.catDot} />{cat}</div>
              {dishes.map((d, i) => (
                <div key={i} style={styles.detailDish}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 4 }}>{d.name}</div>
                  {d.rating > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: d.comment ? 4 : 0 }}>
                      <StarDisplay rating={d.rating} size={14} />
                      <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{LABELS[d.rating]}</span>
                    </div>
                  )}
                  {d.photo && <img src={d.photo} alt={d.name} style={{ width: "100%", borderRadius: 8, marginBottom: d.comment ? 6 : 0, maxHeight: 180, objectFit: "cover" }} />}
                  {d.comment && <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>{d.comment}</div>}
                </div>
              ))}
            </div>
          ))}
          <div style={{ height: 16 }} />
        </div>
        <BottomNav active="friends" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <TopBar subtitle={`${friendUsername}'s visits`} left={<button style={styles.backBtn} onClick={onBack}>‹ Friends</button>} />
      <div style={styles.scrollBody}>

        {/* Star filter */}
        <div style={{ marginBottom: 16 }}>
          <div style={styles.formLabel}>Filter by minimum rating</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[0, 1, 2, 3, 4, 5].map((s) => (
              <span
                key={s}
                onClick={() => setStarFilter(s)}
                style={{
                  padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                  cursor: "pointer", userSelect: "none",
                  border: starFilter === s ? "none" : "0.5px solid var(--color-border-secondary)",
                  background: starFilter === s ? "#C94F2C" : "var(--color-background-secondary)",
                  color: starFilter === s ? "#fff" : "var(--color-text-secondary)",
                }}
              >
                {s === 0 ? "All" : `${s}★+`}
              </span>
            ))}
          </div>
        </div>

        {loading && <Spinner />}

        {!loading && filtered.length === 0 && (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🍽</div>
            <div style={styles.emptyH}>No visits found</div>
            <div style={styles.emptyP}>Try a lower star filter or check back later.</div>
          </div>
        )}

        {!loading && filtered.map((v) => {
          const avg = avgRating(v.dishes);
          const ds = new Date(v.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
          return (
            <div key={v.id} style={styles.card} onClick={() => setSelectedVisit(v)}>
              <div style={styles.cardHeader}>
                <div style={styles.initial}>{v.rest[0].toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.restName}>{v.rest}</div>
                  {v.address && <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginBottom: 2 }}>{v.address}</div>}
                  <div style={styles.cardSub}>{ds} · {v.dishes.length} item{v.dishes.length !== 1 ? "s" : ""}</div>
                  {avg > 0 && (
                    <div style={{ marginTop: 3 }}>
                      <StarDisplay rating={Math.round(avg)} size={13} />
                      <span style={{ fontSize: 12, color: "#888", marginLeft: 5 }}>{avg.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.chips}>
                {v.dishes.slice(0, 5).map((d, i) => (
                  <span key={i} style={styles.chip}>{d.name}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <BottomNav active="friends" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}
// ── MAP SCREEN ───────────────────────────────────────────
const VENUE_COLORS = {
  restaurant: "#C94F2C",
  brewery: "#E8820C",
  winery: "#8B3A8B",
  bar: "#1A6B9A",
};
const VENUE_EMOJIS = { restaurant: "🍽", brewery: "🍺", winery: "🍷", bar: "🍸" };

function MapScreen({ user, onNavigate, friendRequestCount, cachedFriendships }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  const [myVisits, setMyVisits] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [friendVisits, setFriendVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noPoints, setNoPoints] = useState(false);
  const [userLocation, setUserLocation] = useState(null); // {lat, lng}
  const [locationDenied, setLocationDenied] = useState(false);

  const RADIUS_MILES = 50;

  // Haversine distance in miles between two lat/lng points
  function distanceMiles(lat1, lng1, lat2, lng2) {
    const R = 3958.8;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng/2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }

  // Filter state
  const [showMyVisits, setShowMyVisits] = useState(true);
  const [showWaitlist, setShowWaitlist] = useState(true);
  const [showFriends, setShowFriends] = useState(false);
  const [venueFilter, setVenueFilter] = useState([]); // empty = all

  useEffect(() => {
    loadData();
    // Request user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setUserLocation(loc);
          if (mapInstance.current) {
            mapInstance.current.setCenter(loc);
            mapInstance.current.setZoom(11);
          }
        },
        () => setLocationDenied(true),
        { timeout: 8000 }
      );
    } else {
      setLocationDenied(true);
    }
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const { data: visitsData } = await supabase.from("visits").select(VISIT_FIELDS).eq("user_id", user.id);
      setMyVisits(visitsData || []);

      const { data: waitData } = await supabase.from("waitlist").select(WAITLIST_FIELDS).eq("user_id", user.id);
      setWaitlist(waitData || []);

      // Use cached friendships from App, fallback to fetch
      const allFriendships = cachedFriendships?.length ? cachedFriendships
        : (await supabase.from("friendships").select("id, requester_id, addressee_id, status")).data || [];
      const friendIds = getFriendIds(allFriendships, user.id);
      if (friendIds.length > 0) {
        const { data: profiles } = await supabase.from("profiles").select(PROFILE_FIELDS).in("id", friendIds);
        const profileMap = {};
        (profiles || []).forEach(p => profileMap[p.id] = p);
        const { data: fVisits } = await supabase.from("visits").select(VISIT_FIELDS).in("user_id", friendIds);
        setFriendVisits((fVisits || []).map(v => ({
          ...v,
          friendName: getDisplayName(profileMap[v.user_id]),
          friendAvatar: profileMap[v.user_id]?.avatar_url || null,
        })));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  // Geocode an address using Google Maps Geocoder
  function geocodeAddress(address) {
    return new Promise((resolve) => {
      if (!address || !window.google) { resolve(null); return; }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          resolve({ lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() });
        } else { resolve(null); }
      });
    });
  }

  function makeMarkerIconSvg(color, venueType) {
    const emoji = VENUE_EMOJIS[venueType] || "🍽";
    return `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
      <path d="M16 0C7.16 0 0 7.16 0 16c0 10.67 16 24 16 24S32 26.67 32 16C32 7.16 24.84 0 16 0z" fill="${color}"/>
      <circle cx="16" cy="16" r="9" fill="white" opacity="0.9"/>
      <text x="16" y="20" text-anchor="middle" font-size="11">${emoji}</text>
    </svg>`;
  }

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !window.google) return;
    mapInstance.current = new window.google.maps.Map(mapRef.current, {
      center: userLocation || { lat: 40.7128, lng: -74.0060 },
      zoom: 11,
      mapId: "rateyourplate_map",
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: "greedy",
      styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
    });
    infoWindowRef.current = new window.google.maps.InfoWindow({ maxWidth: 200 });
  }, []);

  // Re-center map when location arrives
  useEffect(() => {
    if (!mapInstance.current || !userLocation) return;
    mapInstance.current.setCenter(userLocation);
    mapInstance.current.setZoom(11);
  }, [userLocation]);

  // Place markers whenever filters or data change
  useEffect(() => {
    if (!mapInstance.current || !window.google || loading) return;
    placeMarkers();
  }, [loading, showMyVisits, showWaitlist, showFriends, venueFilter, userLocation]);

  const coordCache = useRef({}); // place_id or address -> {lat, lng}

  async function placeMarkers() {
    // Clear existing markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    async function addMarker(item, type) {
      const vType = item.venue_type || "restaurant";
      if (venueFilter.length > 0 && !venueFilter.includes(vType)) return;

      const cacheKey = item.place_id || item.address;
      let pos = cacheKey ? coordCache.current[cacheKey] : null;

      if (!pos) {
        if (item.place_id) {
          const service = new window.google.maps.places.PlacesService(mapInstance.current);
          pos = await new Promise(resolve => {
            service.getDetails({ placeId: item.place_id, fields: ["geometry"] }, (place, status) => {
              if (status === "OK" && place?.geometry?.location) {
                resolve({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
              } else resolve(null);
            });
          });
        }
        if (!pos && item.address) pos = await geocodeAddress(item.address);
        if (pos && cacheKey) coordCache.current[cacheKey] = pos;
      }
      if (!pos) return;

      // If we have user location, skip pins outside 50 mile radius
      if (userLocation && distanceMiles(userLocation.lat, userLocation.lng, pos.lat, pos.lng) > RADIUS_MILES) return;

      const MARKER_COLORS = {
        visit: "#C94F2C",
        waitlist: "#2563EB",
        friend: "#16A34A",
      };

      const iconDiv = document.createElement("div");
      iconDiv.innerHTML = `<img src="data:image/svg+xml;charset=UTF-8,${encodeURIComponent(makeMarkerIconSvg(MARKER_COLORS[type], vType))}" style="width:32px;height:40px" />`;

      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: pos,
        map: mapInstance.current,
        content: iconDiv,
        title: item.rest,
      });

      const label = type === "waitlist" ? "Want to go"
        : type === "friend" ? `${item.friendName || getDisplayName({email: item.friendEmail})}'s visit`
        : "Your visit";

      marker.addListener("gmp-click", () => {
        infoWindowRef.current.setContent(`
          <div style="font-family:-apple-system,sans-serif;padding:2px 4px;min-width:140px;max-width:200px">
            <div style="font-size:14px;font-weight:600;color:#1a1a1a;margin-bottom:2px;line-height:1.3">${item.rest}</div>
            ${item.address ? `<div style="font-size:11px;color:#888;margin-bottom:4px;line-height:1.3">${item.address}</div>` : ""}
            <div style="font-size:11px;color:#666">${VENUE_EMOJIS[vType]} ${vType.charAt(0).toUpperCase() + vType.slice(1)} · ${label}</div>
            ${item.note ? `<div style="font-size:11px;color:#555;margin-top:3px;font-style:italic">"${item.note}"</div>` : ""}
          </div>
        `);
        infoWindowRef.current.open(mapInstance.current, marker);
      });

      markersRef.current.push(marker);
      bounds.extend(pos);
      hasPoints = true;
    }

    const tasks = [];
    if (showMyVisits) myVisits.forEach(v => tasks.push(addMarker(v, "visit")));
    if (showWaitlist) waitlist.forEach(w => tasks.push(addMarker(w, "waitlist")));
    if (showFriends) friendVisits.forEach(v => tasks.push(addMarker(v, "friend")));

    await Promise.all(tasks);
    if (hasPoints) {
      setNoPoints(false);
      mapInstance.current.fitBounds(bounds, { top: 60, right: 40, bottom: 60, left: 40 });
      const listener = window.google.maps.event.addListener(mapInstance.current, "idle", () => {
        if (mapInstance.current.getZoom() > 15) mapInstance.current.setZoom(15);
        window.google.maps.event.removeListener(listener);
      });
    } else {
      setNoPoints(true);
    }
  }

  const toggleVenueFilter = (id) => {
    setVenueFilter(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  const FilterPill = ({ label, active, onClick, color }) => (
    <span onClick={onClick} style={{
      padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500,
      cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
      border: active ? "none" : "0.5px solid var(--color-border-secondary)",
      background: active ? (color || "#C94F2C") : "var(--color-background-secondary)",
      color: active ? "#fff" : "var(--color-text-secondary)",
    }}>{label}</span>
  );

  return (
    <div style={{ ...styles.screen, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <TopBar subtitle="Map" />

      {/* Filter bar - two rows */}
      <div style={{ flexShrink: 0, padding: "8px 16px", borderBottom: "0.5px solid var(--color-border-tertiary)", background: "var(--color-background-primary)" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "nowrap", overflowX: "auto" }}>
          <FilterPill label="My visits" active={showMyVisits} onClick={() => setShowMyVisits(!showMyVisits)} color="#C94F2C" />
          <FilterPill label="Wishlist" active={showWaitlist} onClick={() => setShowWaitlist(!showWaitlist)} color="#2563EB" />
          <FilterPill label="Friends" active={showFriends} onClick={() => setShowFriends(!showFriends)} color="#16A34A" />
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", overflowX: "auto" }}>
          {VENUE_TYPES.map(v => (
            <FilterPill key={v.id} label={`${v.emoji} ${v.label}`}
              active={venueFilter.includes(v.id)}
              onClick={() => toggleVenueFilter(v.id)}
              color={VENUE_COLORS[v.id]} />
          ))}
        </div>
      </div>

      {/* Map — fills remaining space */}
      <div style={{ flex: 1, position: "relative", minHeight: 0, overflow: "hidden" }}>
        {loading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--color-background-primary)", zIndex: 1 }}><Spinner /></div>}
        {!loading && noPoints && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "var(--color-background-primary)", zIndex: 1, padding: 32 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🗺</div>
            <div style={styles.emptyH}>No spots to show</div>
            <div style={styles.emptyP}>
              {userLocation
                ? `No venues found within ${RADIUS_MILES} miles of your location. Try turning on more filters or add some visits first.`
                : "Try turning on more filters, or add some visits and wishlist items first."}
            </div>
          </div>
        )}
        <div ref={mapRef} style={{ position: "absolute", inset: 0 }} />
      </div>

      <BottomNav active="map" onNavigate={onNavigate} friendRequestCount={friendRequestCount} />
    </div>
  );
}

function BottomNav({ active, onNavigate, friendRequestCount = 0 }) {
  const newFriendCount = (() => {
    try { return JSON.parse(sessionStorage.getItem("ryp_new_friends") || "[]").length; } catch { return 0; }
  })();
  const items = [
    { id: "venues", icon: "🍽", label: "Venues" },
    { id: "map", icon: "🗺", label: "Map" },
    { id: "friends", icon: "👥", label: "Friends" },
    { id: "profile", icon: "👤", label: "Profile" },
  ];
  return (
    <div style={styles.bottomNav}>
      {items.map((item) => (
        <div
          key={item.id}
          onClick={() => onNavigate && onNavigate(item.id)}
          style={{
            ...styles.navItem,
            background: active === item.id ? "#FAECE7" : "transparent",
            borderRadius: 12,
            margin: "4px 6px",
          }}
        >
          <div style={{ position: "relative", display: "inline-block" }}>
            <div style={{ fontSize: 28 }}>{item.icon}</div>
            {item.id === "friends" && (friendRequestCount > 0 || newFriendCount > 0) && (
              <div style={{
                position: "absolute", top: -2, right: -4,
                width: 10, height: 10, borderRadius: "50%",
                background: newFriendCount > 0 ? "#3B6D11" : "#C94F2C",
                border: "2px solid var(--color-background-primary)",
              }} />
            )}
          </div>
          <div style={{
            fontSize: 11,
            color: active === item.id ? "#C94F2C" : "var(--color-text-tertiary)",
            fontWeight: active === item.id ? 500 : 400,
            lineHeight: 1.2,
            textAlign: "center",
            whiteSpace: "nowrap",
          }}>
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── PWA INSTALL PROMPT ────────────────────────────────────
function InstallPrompt() {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("ryp_install_dismissed")) return;
    // Android/Chrome — native install prompt
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      setPrompt(e);
      setShow(true);
    });
    // iOS Safari — show manual instructions
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isInStandaloneMode = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIos && !isInStandaloneMode) setShowIos(true);
  }, []);

  function dismiss() {
    setShow(false);
    setShowIos(false);
    localStorage.setItem("ryp_install_dismissed", "1");
  }

  const bannerStyle = { position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", width: "calc(100% - 32px)", maxWidth: 448, zIndex: 200, background: "#fff", borderRadius: 16, padding: "14px 16px", boxShadow: "0 4px 24px rgba(0,0,0,0.15)", border: "0.5px solid var(--color-border-secondary)", display: "flex", alignItems: "center", gap: 12 };

  if (show && prompt) return (
    <div style={bannerStyle}>
      <img src="/favicon.ico" alt="" style={{ width: 36, height: 36, borderRadius: 8 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>Add to Home Screen</div>
        <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>Install Rate Your Plate for quick access</div>
      </div>
      <button onClick={() => { prompt.prompt(); setShow(false); }} style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: "#C94F2C", border: "none", borderRadius: 20, padding: "7px 14px", cursor: "pointer" }}>Install</button>
      <button onClick={dismiss} style={{ fontSize: 18, color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
    </div>
  );

  if (showIos) return null;

  return null;
}

// ── ONBOARDING SCREEN ─────────────────────────────────────
const ONBOARDING_SLIDES = [
  {
    emoji: "🍽",
    title: "Track every meal",
    body: "Log your visits, rate dishes, add photos, and build a personal dining journal you'll actually use.",
    bg: "#FFF5F2",
    accent: "#C94F2C",
  },
  {
    emoji: "👥",
    title: "See where friends eat",
    body: "Follow friends and see their ratings in real time. Discover where the people you trust are dining.",
    bg: "#F0FDF4",
    accent: "#16A34A",
  },
  {
    emoji: "📋",
    title: "Build your wishlist",
    body: "Save restaurants you want to try. Never forget a recommendation again.",
    bg: "#EEF2FF",
    accent: "#4338CA",
  },
];

function OnboardingScreen({ onDone }) {
  const [slide, setSlide] = useState(0);
  const touchStartX = useRef(null);

  function next() {
    if (slide < ONBOARDING_SLIDES.length - 1) setSlide(slide + 1);
    else finish();
  }

  function finish() {
    localStorage.setItem("ryp_onboarded", "1");
    onDone();
  }

  function handleTouchStart(e) { touchStartX.current = e.touches[0].clientX; }
  function handleTouchEnd(e) {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 40) next();
    else if (diff < -40 && slide > 0) setSlide(slide - 1);
    touchStartX.current = null;
  }

  const s = ONBOARDING_SLIDES[slide];

  return (
    <div style={{ ...styles.screen, background: s.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", padding: "60px 32px 48px", transition: "background 0.3s" }}
      onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>

      {/* Top spacer to replace where skip was */}
      <div style={{ width: "100%" }} />

      {/* Content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
        <div style={{ fontSize: 80, marginBottom: 32, lineHeight: 1 }}>{s.emoji}</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: s.accent, marginBottom: 16, lineHeight: 1.2 }}>{s.title}</div>
        <div style={{ fontSize: 16, color: "#555", lineHeight: 1.6, maxWidth: 300 }}>{s.body}</div>
      </div>

      {/* Dots */}
      <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
        {ONBOARDING_SLIDES.map((_, i) => (
          <div key={i} onClick={() => setSlide(i)} style={{ width: i === slide ? 24 : 8, height: 8, borderRadius: 4, background: i === slide ? s.accent : "#ccc", cursor: "pointer", transition: "all 0.2s" }} />
        ))}
      </div>

      {/* Button + Skip */}
      <div style={{ width: "100%" }}>
        <button onClick={next} style={{ width: "100%", padding: "16px", borderRadius: 14, background: s.accent, color: "#fff", fontSize: 16, fontWeight: 600, border: "none", cursor: "pointer" }}>
          {slide < ONBOARDING_SLIDES.length - 1 ? "Next" : "Get started"}
        </button>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <button onClick={finish} style={{ fontSize: 14, color: s.accent, background: "none", border: "none", cursor: "pointer", opacity: 0.6, padding: "4px 0" }}>Skip</button>
        </div>
      </div>
    </div>
  );
}

// ── VISIT SURVEY ─────────────────────────────────────────
const SURVEY_STEPS = [
  {
    key: "would_return",
    question: "Would you return?",
    type: "choice",
    options: ["Yes", "Maybe", "No"],
  },
  {
    key: "wait_time",
    question: "How was the wait?",
    type: "choice",
    options: ["No wait", "Short", "Moderate", "Long"],
  },
  {
    key: "service_rating",
    question: "How was the service?",
    type: "stars",
  },
  {
    key: "occasion",
    question: "What was the occasion?",
    type: "choice",
    options: ["Casual", "Date Night", "Girls/Guys Night", "Business", "Special Occasion"],
  },
  {
    key: "would_recommend",
    question: "Would you recommend it?",
    type: "choice",
    options: ["Yes", "Maybe", "No"],
  },
];

function VisitSurvey({ visit, onDone }) {
  const [step, setStep] = useState(-1); // -1 = intro, 0-4 = questions, 5 = done
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  async function saveAnswer(key, value) {
    const newAnswers = { ...answers, [key]: value };
    setAnswers(newAnswers);

    // Save incrementally
    await supabase.from("visits").update({ [key]: value }).eq("id", visit.id);

    const nextStep = step + 1;
    if (nextStep >= SURVEY_STEPS.length) {
      // All done
      setSaving(true);
      await supabase.from("visits").update({ survey_completed_at: new Date().toISOString() }).eq("id", visit.id);
      setSaving(false);
      onDone(newAnswers);
    } else {
      setStep(nextStep);
    }
  }

  async function skip() {
    // Mark as seen so we don't prompt again
    await supabase.from("visits").update({ survey_completed_at: new Date().toISOString() }).eq("id", visit.id);
    onDone({});
  }

  const currentStep = SURVEY_STEPS[step];

  return (
    <div style={styles.screen}>
      <TopBar subtitle={visit.rest} left={<button style={styles.backBtn} onClick={skip}>‹ Back</button>} />
      <div style={styles.scrollBody}>

        {step === -1 ? (
          // Intro
          <div style={{ padding: "32px 8px" }}>
            <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", marginBottom: 4 }}>Quick questions about</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "var(--color-text-primary)", marginBottom: 8 }}>{visit.rest}</div>
            <div style={{ fontSize: 15, color: "var(--color-text-secondary)", marginBottom: 32, lineHeight: 1.6 }}>Takes about 20 seconds. Helps your friends know what to expect.</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep(0)} style={{ flex: 1, padding: "14px", borderRadius: 12, background: "#C94F2C", color: "#fff", fontSize: 15, fontWeight: 600, border: "none", cursor: "pointer" }}>
                Sure! 🍽
              </button>
              <button onClick={skip} style={{ flex: 1, padding: "14px", borderRadius: 12, background: "var(--color-background-secondary)", color: "var(--color-text-secondary)", fontSize: 15, fontWeight: 500, border: "0.5px solid var(--color-border-secondary)", cursor: "pointer" }}>
                Skip
              </button>
            </div>
          </div>
        ) : (
          // Question steps
          <div style={{ padding: "24px 8px" }}>
            {/* Progress dots */}
            <div style={{ display: "flex", gap: 6, marginBottom: 32, justifyContent: "center" }}>
              {SURVEY_STEPS.map((_, i) => (
                <div key={i} style={{ width: i === step ? 20 : 8, height: 8, borderRadius: 4, background: i <= step ? "#C94F2C" : "var(--color-border-secondary)", transition: "all 0.2s" }} />
              ))}
            </div>

            <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>{visit.rest}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "var(--color-text-primary)", marginBottom: 24 }}>{currentStep.question}</div>

            {currentStep.type === "choice" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {currentStep.options.map(opt => (
                  <button key={opt} onClick={() => saveAnswer(currentStep.key, opt)}
                    style={{ padding: "14px 16px", borderRadius: 12, background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-secondary)", fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)", cursor: "pointer", textAlign: "left" }}>
                    {opt}
                  </button>
                ))}
              </div>
            )}

            {currentStep.type === "stars" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
                  {[1,2,3,4,5].map(s => (
                    <span key={s} onClick={() => saveAnswer(currentStep.key, s)}
                      style={{ fontSize: 40, cursor: "pointer" }}>
                      ⭐
                    </span>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--color-text-tertiary)" }}>
                  <span>Poor</span><span>Amazing</span>
                </div>
              </div>
            )}

            <button onClick={skip} style={{ width: "100%", marginTop: 24, padding: "12px", borderRadius: 10, background: "none", border: "none", fontSize: 13, color: "var(--color-text-tertiary)", cursor: "pointer" }}>
              Skip remaining questions
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── GETTING STARTED CARD ──────────────────────────────────
function GettingStartedCard({ visits, wishlist, friends, onAddSpot, onAddFriend }) {
  const done = {
    visit: visits.length > 0,
    wishlist: wishlist.length > 0,
    friend: friends > 0,
  };
  const allDone = done.visit && done.wishlist && done.friend;
  if (allDone) {
    localStorage.setItem("ryp_checklist_done", "1");
    return null;
  }
  if (localStorage.getItem("ryp_checklist_done")) return null;

  const steps = [
    { key: "visit", label: "Log your first visit", cta: "Add a Spot", action: onAddSpot },
    { key: "wishlist", label: "Add a spot to your wishlist", cta: "Add a Spot", action: onAddSpot },
    { key: "friend", label: "Add your first friend", cta: "Go to Friends", action: onAddFriend },
  ];

  const completed = Object.values(done).filter(Boolean).length;

  return (
    <div style={{ margin: "0 0 14px", padding: "14px 16px", borderRadius: 16, background: "#FFF5F2", border: "1.5px solid #FAECE7" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#C94F2C" }}>🚀 Getting started</div>
        <div style={{ fontSize: 12, color: "#C94F2C", opacity: 0.7 }}>{completed}/3</div>
      </div>
      {steps.map(step => (
        <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: done[step.key] ? "#C94F2C" : "#fff", border: `2px solid ${done[step.key] ? "#C94F2C" : "#ddd"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#fff", fontWeight: 700 }}>
            {done[step.key] ? "✓" : ""}
          </div>
          <div style={{ flex: 1, fontSize: 13, color: done[step.key] ? "#999" : "var(--color-text-primary)", textDecoration: done[step.key] ? "line-through" : "none" }}>{step.label}</div>
          {!done[step.key] && (
            <button onClick={step.action} style={{ fontSize: 11, color: "#C94F2C", background: "none", border: "1px solid #C94F2C", borderRadius: 20, padding: "3px 10px", cursor: "pointer", fontWeight: 500, whiteSpace: "nowrap" }}>{step.cta}</button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── AUTH SCREEN ───────────────────────────────────────────
function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("join") ? "signup" : "login";
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [inviteCode, setInviteCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("join") || "";
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function checkUsername(name) {
    if (!name.trim()) return false;
    const { data } = await supabase.from("profiles").select("id").eq("username", name.trim().toLowerCase()).maybeSingle();
    return !data; // true = available
  }

  async function handleSubmit() {
    if (!email.trim() || !password.trim()) { setError("Please enter your email and password."); return; }
    if (mode === "signup" && !username.trim()) { setError("Please choose a username."); return; }
    if (mode === "signup" && !/^[a-z0-9_]{2,20}$/.test(username.trim().toLowerCase())) {
      setError("Username must be 2-20 characters, letters, numbers, and underscores only.");
      return;
    }
    setLoading(true); setError(""); setMessage("");
    try {
      if (mode === "signup") {
        // Check username availability
        const available = await checkUsername(username.trim().toLowerCase());
        if (!available) { setError("That username is already taken. Please choose another."); setLoading(false); return; }

        const { error: signUpError } = await supabase.auth.signUp({ email, password });
        if (signUpError) throw signUpError;

        const { data, error: loginError } = await supabase.auth.signInWithPassword({ email, password });
        if (loginError) throw loginError;

        // Save username
        const profileUpdate = { id: data.user.id, email: data.user.email, username: username.trim().toLowerCase() };

        // Upload avatar if provided
        if (avatarFile) {
          try {
            const ext = avatarFile.name.split(".").pop();
            const path = `${data.user.id}/avatar.${ext}`;
            const { error: uploadError } = await supabase.storage.from("avatars").upload(path, avatarFile, { upsert: true });
            if (!uploadError) {
              const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(path);
              profileUpdate.avatar_url = publicUrl;
            }
          } catch (e) { console.error("Avatar upload failed:", e); }
        }

        await supabase.from("profiles").upsert(profileUpdate, { onConflict: "id" });

        // Handle invite code
        const code = inviteCode.trim().toUpperCase();
        if (code) {
          try {
            const { data: invite } = await supabase.from("invite_codes").select("user_id").eq("code", code).maybeSingle();
            if (invite && invite.user_id !== data.user.id) {
              await supabase.from("friendships").insert({ requester_id: invite.user_id, addressee_id: data.user.id, status: "accepted" });
            }
          } catch (e) { console.error("Invite code error:", e); }
        }

        onAuth(data.user);
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onAuth(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ ...styles.screen, overflowY: "auto" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start", padding: "60px 24px 40px", minHeight: "100%" }}>
        <div style={{ marginBottom: 40, textAlign: "center" }}>
          <div style={{ ...styles.logo, fontSize: 32, marginBottom: 6 }}>Rate Your Plate</div>
          <div style={{ fontSize: 14, color: "var(--color-text-secondary)" }}>Track and share your dining experiences</div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={styles.formLabel}>Email</div>
          <input style={styles.input} type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()} autoCapitalize="none" />
        </div>

        <div style={{ marginBottom: mode === "signup" ? 14 : 20 }}>
          <div style={styles.formLabel}>Password</div>
          <input style={styles.input} type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()} />
        </div>

        {mode === "signup" && (
          <>
            {/* Avatar picker */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
              <label style={{ cursor: "pointer", textAlign: "center" }}>
                <input type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => { const f = e.target.files[0]; if (!f) return; if (!["image/jpeg", "image/png", "image/webp", "image/heic"].includes(f.type)) { alert("Please upload a JPG, PNG, or WebP image."); return; } if (f.size > 3 * 1024 * 1024) { alert("Photo must be under 3MB."); return; } setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)); }} />
                <div style={{ position: "relative", display: "inline-block" }}>
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="avatar" style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", border: "3px solid #C94F2C" }} />
                  ) : (
                    <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#FAECE7", border: "2px dashed #C94F2C", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>👤</div>
                  )}
                  <div style={{ position: "absolute", bottom: 0, right: 0, width: 22, height: 22, borderRadius: "50%", background: "#C94F2C", border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>📷</div>
                </div>
                <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 6 }}>Add photo (optional)</div>
              </label>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={styles.formLabel}>Username <span style={{ color: "#C94F2C" }}>*</span></div>
              <input style={styles.input} type="text" value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                placeholder="e.g. foodie_joe" autoCapitalize="none" autoCorrect="off" />
              <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 4 }}>
                2-20 characters, letters, numbers, underscores. This is how friends find you.
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={styles.formLabel}>Invite code (optional)</div>
              <input style={styles.input} type="text" value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="e.g. ABC123" autoCapitalize="characters" autoCorrect="off" />
            </div>
          </>
        )}

        {error && <div style={{ fontSize: 13, color: "#C94F2C", marginBottom: 14, lineHeight: 1.5 }}>{error}</div>}
        {message && <div style={{ fontSize: 13, color: "#3B6D11", marginBottom: 14, lineHeight: 1.5 }}>{message}</div>}

        <button style={{ ...styles.saveVisitBtn, opacity: loading ? 0.7 : 1 }} onClick={handleSubmit} disabled={loading}>
          {loading ? "Please wait..." : mode === "login" ? "Log in" : "Create account"}
        </button>

        <div style={{ textAlign: "center", marginTop: 18 }}>
          <span style={{ fontSize: 14, color: "var(--color-text-secondary)" }}>
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
          </span>
          <span style={{ fontSize: 14, color: "#C94F2C", fontWeight: 500, cursor: "pointer" }}
            onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); setMessage(""); }}>
            {mode === "login" ? "Sign up" : "Log in"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── PROFILE SCREEN ────────────────────────────────────────
function ProfileScreen({ user, onSignOut, onClose, onNavigate, friendRequestCount }) {
  const [signingOut, setSigningOut] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [copied, setCopied] = useState(false);

  const [username, setUsername] = useState("");
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameSaving, setUsernameSaving] = useState(false);

  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  useEffect(() => {
    supabase.from("profiles").select("username, avatar_url").eq("id", user.id).single()
      .then(({ data }) => {
        if (data?.username) { setUsername(data.username); setNewUsername(data.username); }
        if (data?.avatar_url) setAvatarUrl(data.avatar_url);
      });
  }, []);

  async function handleAvatarChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/heic"].includes(file.type)) { alert("Please upload a JPG, PNG, or WebP image."); return; }
    if (file.size > 3 * 1024 * 1024) { alert("Photo must be under 3MB."); return; }
    setAvatarUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${user.id}/avatar.${ext}`;
      const { error: uploadError } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(path);
      await supabase.from("profiles").update({ avatar_url: publicUrl }).eq("id", user.id);
      setAvatarUrl(publicUrl + "?t=" + Date.now()); // cache bust
    } catch (err) { alert("Error uploading photo: " + err.message); }
    finally { setAvatarUploading(false); }
  }

  useEffect(() => {
    if (showInvite) generateCode();
  }, [showInvite]);

  async function generateCode() {
    // Check if user already has a code
    const { data: existing } = await supabase.from("invite_codes").select("code").eq("user_id", user.id).maybeSingle();
    if (existing) { setInviteCode(existing.code); return; }
    // Generate a new 6-character code
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    await supabase.from("invite_codes").insert({ code, user_id: user.id });
    setInviteCode(code);
  }

  async function handleSignOut() {
    setSigningOut(true);
    await supabase.auth.signOut();
    onSignOut();
  }

  function handleSendEmail() {
    const subject = encodeURIComponent("You should try Rate Your Plate");
    const body = encodeURIComponent(`Hey! I've been using this app called Rate Your Plate to track and share restaurants, dishes, and drinks with friends. You should check it out!\n\nhttps://rateyourplateapp.com?join=${inviteCode}`);
    window.open(`mailto:${inviteEmail}?subject=${subject}&body=${body}`);
  }

  function handleCopy() {
    const text = `Hey! I've been using this app called Rate Your Plate to track and share restaurants, dishes, and drinks with friends. You should check it out!\n\nhttps://rateyourplateapp.com?join=${inviteCode}`;
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  async function saveUsername() {
    const val = newUsername.trim().toLowerCase();
    if (!val) { setUsernameError("Username cannot be empty."); return; }
    if (!/^[a-z0-9_]{2,20}$/.test(val)) { setUsernameError("2-20 chars, letters/numbers/underscores only."); return; }
    if (val === username) { setEditingUsername(false); return; }
    setUsernameSaving(true); setUsernameError("");
    try {
      const { data: existing } = await supabase.from("profiles").select("id").eq("username", val).maybeSingle();
      if (existing && existing.id !== user.id) { setUsernameError("That username is already taken."); return; }
      await supabase.from("profiles").update({ username: val }).eq("id", user.id);
      setUsername(val);
      setEditingUsername(false);
    } catch (e) { setUsernameError("Error saving username."); }
    finally { setUsernameSaving(false); }
  }

  // Invite sub-screen
  if (showInvite) {
    return (
      <div style={styles.screen}>
        <TopBar subtitle="Invite a friend" left={<button style={styles.backBtn} onClick={() => setShowInvite(false)}>‹ Profile</button>} />
        <div style={styles.scrollBody}>

          {/* Invite code display */}
          <div style={{ textAlign: "center", marginBottom: 24, padding: "20px", background: "var(--color-background-secondary)", borderRadius: 16, border: "0.5px solid var(--color-border-secondary)" }}>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 500 }}>Your invite code</div>
            <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: 6, color: "#C94F2C", fontFamily: "monospace" }}>
              {inviteCode || "..."}
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 8 }}>
              Share this with friends — they enter it when signing up
            </div>
          </div>

          <div style={styles.dbField}>
            <div style={styles.dbLabel}>To (optional)</div>
            <input style={styles.input} type="email" placeholder="friend@email.com"
              value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
              autoCapitalize="none" autoCorrect="off" />
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button style={{ ...styles.pillBtn, flex: 1 }} onClick={handleSendEmail}>📧 Open in email</button>
            <button style={{ ...styles.pillBtn, flex: 1, background: copied ? "#3B6D11" : "#C94F2C" }} onClick={handleCopy}>
              {copied ? "✓ Copied!" : "📋 Copy"}
            </button>
          </div>
        </div>
        <BottomNav active="profile" onNavigate={(tab) => { setShowInvite(false); onClose(); onNavigate(tab); }} friendRequestCount={friendRequestCount} />
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <TopBar subtitle="Profile" />
      <div style={styles.scrollBody}>

        {/* User info */}
        <div style={styles.detailHeader}>
          {/* Tappable avatar */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <label style={{ position: "relative", cursor: "pointer" }}>
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarChange} />
              {avatarUrl ? (
                <img src={avatarUrl} alt="avatar"
                  style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", border: "3px solid #C94F2C" }} />
              ) : (
                <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#FAECE7", border: "3px solid #C94F2C", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>👤</div>
              )}
              <div style={{ position: "absolute", bottom: 0, right: 0, width: 22, height: 22, borderRadius: "50%", background: "#C94F2C", border: "2px solid var(--color-background-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>
                {avatarUploading ? "⏳" : "📷"}
              </div>
            </label>
          </div>
          {!editingUsername ? (
            <div style={{ textAlign: "center", marginBottom: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 600, color: "var(--color-text-primary)" }}>@{username || user.email?.split("@")[0]}</span>
              <button onClick={() => { setEditingUsername(true); setNewUsername(username); setUsernameError(""); }}
                style={{ marginLeft: 8, fontSize: 12, color: "#C94F2C", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Edit</button>
            </div>
          ) : (
            <div style={{ marginBottom: 8 }}>
              <input style={{ ...styles.input, textAlign: "center", fontSize: 15 }}
                value={newUsername}
                onChange={e => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                autoCapitalize="none" autoCorrect="off" autoFocus />
              {usernameError && <div style={{ fontSize: 12, color: "#C94F2C", marginTop: 4, textAlign: "center" }}>{usernameError}</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button style={{ ...styles.pillBtn, flex: 1, opacity: usernameSaving ? 0.7 : 1 }} onClick={saveUsername} disabled={usernameSaving}>
                  {usernameSaving ? "Saving..." : "Save"}
                </button>
                <button style={{ ...styles.pillBtnGhost, flex: 1 }} onClick={() => { setEditingUsername(false); setUsernameError(""); }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ textAlign: "center", fontSize: 12, color: "var(--color-text-tertiary)" }}>
            {user.email}
          </div>
          <div style={{ textAlign: "center", fontSize: 12, color: "var(--color-text-tertiary)" }}>
            Member since {new Date(user.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </div>
          <div style={{ textAlign: "center", fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 4 }}>
            v0.1.24
          </div>
        </div>

        <div style={styles.divider} />

        <button style={styles.pillBtn} onClick={() => setShowInvite(true)}>
          ✉️ Invite a friend
        </button>

        <div style={styles.divider} />

        <button
          style={{ ...styles.pillBtnGhost, width: "100%", opacity: signingOut ? 0.7 : 1 }}
          onClick={handleSignOut}
          disabled={signingOut}
        >
          {signingOut ? "Signing out..." : "Sign out"}
        </button>
      </div>
      <BottomNav active="profile" onNavigate={(tab) => { onClose(); onNavigate(tab); }} friendRequestCount={friendRequestCount} />
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dbWaking, setDbWaking] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [screen, setScreen] = useState(() => {
    const saved = localStorage.getItem("ryp_screen");
    return saved && ["venues","map","friends"].includes(saved) ? saved : "venues";
  });
  const [detailId, setDetailId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [restKey, setRestKey] = useState(null);
  const [dishInfo, setDishInfo] = useState(null);
  const [friendId, setFriendId] = useState(null);
  const [friendEmail, setFriendEmail] = useState("");
  const [incomingCount, setIncomingCount] = useState(0);
  const [friendships, setFriendships] = useState([]);
  const [newFriendCount, setNewFriendCount] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("ryp_new_friends") || "[]").length; } catch { return 0; }
  });
  const [showFriendSettings, setShowFriendSettings] = useState(false);
  const [friendSettingsPrefill, setFriendSettingsPrefill] = useState("");
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [friendVisits, setFriendVisits] = useState([]);
  const [pendingSurveyVisit, setPendingSurveyVisit] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem("ryp_onboarded"));

  useEffect(() => {
    // Capture invite parameter from hash — query strings get stripped by email clients
    const hash = window.location.hash; // e.g. "#i=ABC123"
    const encoded = hash.startsWith("#i=") ? hash.slice(3) : null;
    const hasInvite = !!encoded;

    if (encoded) {
      try {
        const ref = atob(encoded);
        localStorage.setItem("ryp_ref", ref);
        console.log("Stored invite ref:", ref);
      } catch (e) { console.error("Invalid invite param"); }
      window.history.replaceState({}, "", window.location.pathname);
      supabase.auth.signOut();
    }

    // Always set up auth listener — handles signup, login, and tab restore
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (session?.user) upsertProfile(session.user);
    });

    // Check for existing session — skip if handling invite link
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!hasInvite) {
        setUser(session?.user ?? null);
        if (session?.user) upsertProfile(session.user);
      }
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Keepalive ping every 4 minutes to prevent Supabase cold starts
  useEffect(() => {
    if (!user) return;
    const ping = () => supabase.from("profiles").select("id").eq("id", user.id).single();
    const interval = setInterval(ping, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  async function upsertProfile(u, username = null) {
    const updateData = { id: u.id, email: u.email };
    if (username) updateData.username = username;
    await supabase.from("profiles").upsert(updateData, { onConflict: "id", ignoreDuplicates: false });

    // Fetch full profile and merge into user state
    const { data: profile } = await supabase.from("profiles").select("username").eq("id", u.id).single();
    if (profile?.username) setUser(prev => prev ? { ...prev, username: profile.username } : prev);

    // Check for invite code — auto-friend if coming from an invite
    const code = localStorage.getItem("ryp_invite_code");
    if (code) {
      try {
        const { data: invite } = await supabase.from("invite_codes").select("user_id").eq("code", code).maybeSingle();
        if (invite && invite.user_id !== u.id) {
          const { data: existing } = await supabase.from("friendships").select("id")
            .or(`and(requester_id.eq.${u.id},addressee_id.eq.${invite.user_id}),and(requester_id.eq.${invite.user_id},addressee_id.eq.${u.id})`)
            .maybeSingle();
          if (!existing) {
            await supabase.from("friendships").insert({
              requester_id: invite.user_id,
              addressee_id: u.id,
              status: "accepted",
            });
            console.log("Auto-friended via invite code:", code);
          }
        }
      } catch (e) { console.error("Invite code error:", e); }
      localStorage.removeItem("ryp_invite_code");
    }

    const { data } = await supabase.from("friendships").select("id, requester_id, addressee_id, status").eq("addressee_id", u.id).eq("status", "pending");
    setIncomingCount((data || []).length);

    // Load and cache all friendships for this user
    const { data: allFs } = await supabase.from("friendships").select("id, requester_id, addressee_id, status")
      .or(`requester_id.eq.${u.id},addressee_id.eq.${u.id}`);
    setFriendships(allFs || []);
  }

  async function loadVisits(userId) {
    setLoading(true);
    const start = Date.now();
    try {
      // 15 second timeout — if DB doesn't respond, fail gracefully
      const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 15000));
      const [visitsRes, dishesRes] = await Promise.race([
        Promise.all([
          supabase.from("visits").select(VISIT_FIELDS).eq("user_id", userId || user.id).order("date", { ascending: false }),
          supabase.from("dishes").select("id, visit_id, name, rating, cat, comment, beer_type, abv, user_id").eq("user_id", userId || user.id),
        ]),
        timeout.then(() => { throw new Error("timeout"); }),
      ]);
      if (visitsRes.error) throw visitsRes.error;
      if (dishesRes.error) throw dishesRes.error;
      const combined = visitsRes.data.map((v) => ({
        ...v,
        dishes: dishesRes.data.filter((d) => d.visit_id === v.id),
      }));
      setVisits(combined);
      if (Date.now() - start > 3000) setDbWaking(false);
    } catch (err) {
      const isTimeout = err.message === "timeout";
      setLoadError(isTimeout ? "Taking too long to load. Check your connection and try again." : "Failed to load: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    // Check if this might be a cold start and show friendly message
    const lastActive = localStorage.getItem("ryp_last_active");
    const now = Date.now();
    if (!lastActive || now - parseInt(lastActive) > 5 * 60 * 1000) {
      setDbWaking(true);
    }
    localStorage.setItem("ryp_last_active", now.toString());
    loadVisits(user.id);
  }, [user]);

  async function handleDeleteVisit(id) {
    if (!window.confirm("Delete this visit? This cannot be undone.")) return;
    try {
      const { error } = await supabase.from("visits").delete().eq("id", id);
      if (error) throw error;
      setVisits(visits.filter((v) => v.id !== id));
      if (screen === "detail") setScreen("venues");
    } catch (err) {
      alert("Error deleting visit: " + err.message);
    }
  }

  function openDetail(id) { setDetailId(id); setScreen("detail"); }
  function openEdit(id) { setEditId(id); setScreen("edit"); }
  function openRestaurant(key) { setRestKey(key); setScreen("restaurant-detail"); }
  function openDish(info) { setDishInfo(info); setScreen("dish-reviews"); }
  function openFriendVisits(id, email) { setFriendId(id); setFriendEmail(email); setScreen("friend-visits"); }

  function handleNavigate(tab) {
    const validTabs = ["venues", "friends", "map", "profile"];
    if (validTabs.includes(tab)) {
      if (tab === "profile") { setShowProfile(true); return; }
      localStorage.setItem("ryp_screen", tab);
      setScreen(tab);
      if (tab === "friends") {
        setIncomingCount(0);
        setNewFriendCount(0);
        sessionStorage.setItem("ryp_new_friends", "[]");
      }
    }
  }

  const detailVisit = visits.find((v) => v.id === detailId);
  const editVisit = visits.find((v) => v.id === editId);

  if (authLoading) {
    return (
      <div style={{ ...styles.appShell }}>
        <div style={{
          width: "100%", maxWidth: 480, height: "100dvh", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", position: "fixed", left: "50%", transform: "translateX(-50%)",
          background: "var(--color-background-primary)",
        }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>🍽</div>
          <div style={{ ...styles.logo, fontSize: 28, marginBottom: 16 }}>Rate Your Plate</div>
          <Spinner />
        </div>
      </div>
    );
  }

  if (!user && showOnboarding) {
    return (
      <div style={styles.appShell}>
        <OnboardingScreen onDone={() => setShowOnboarding(false)} />
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.appShell}>
        <AuthScreen onAuth={setUser} />
      </div>
    );
  }

  return (
    <div style={styles.appShell}>
      <InstallPrompt />
      {/* Visit survey — full screen, replaces everything */}
      {pendingSurveyVisit && (
        <VisitSurvey
          visit={pendingSurveyVisit}
          onDone={() => { setPendingSurveyVisit(null); loadVisits(user.id); }}
        />
      )}
      {!pendingSurveyVisit && showProfile ? (
        <ProfileScreen
          user={user}
          onSignOut={() => { setVisits([]); setScreen("venues"); setUser(null); setShowProfile(false); }}
          onClose={() => setShowProfile(false)}
          onNavigate={handleNavigate}
          friendRequestCount={incomingCount}
        />
      ) : !pendingSurveyVisit ? (
        <>
          {screen === "venues" && (
            <VenuesScreen
              visits={visits} user={user} loading={loading}
              onNewVisit={() => setScreen("add")}
              onOpenVisit={openDetail}
              onOpenRestaurant={openRestaurant}
              onNavigate={handleNavigate}
              friendRequestCount={incomingCount}
              onRefresh={() => loadVisits(user.id)}
              cachedFriendships={friendships}
            />
          )}
          {screen === "add" && (
            <AddVisitScreen
              onSave={(newVisit) => { loadVisits(user.id); setScreen("venues"); if (newVisit) setTimeout(() => setPendingSurveyVisit(newVisit), 600); }}
              onCancel={() => setScreen("venues")}
              user={user}
              onNavigate={handleNavigate}
              friendRequestCount={incomingCount}
            />
          )}
          {screen === "detail" && detailVisit && (
            <DetailScreen
              visit={detailVisit}
              onBack={() => setScreen("venues")}
              onEdit={openEdit}
              onDelete={handleDeleteVisit}
              onNavigate={handleNavigate}
              friendRequestCount={incomingCount}
              user={user}
            />
          )}
          {screen === "edit" && editVisit && (
            <EditVisitScreen
              visit={editVisit}
              onSave={() => { loadVisits(user.id); setScreen("venues"); }}
              onCancel={() => setScreen(detailId ? "detail" : "venues")}
              user={user}
              onNavigate={handleNavigate}
              friendRequestCount={incomingCount}
            />
          )}
          {screen === "restaurant-detail" && restKey && (
            <RestaurantDetailScreen
              visits={visits} friendVisits={friendVisits} restKey={restKey}
              user={user}
              onBack={() => setScreen("venues")}
              onOpenDish={openDish}
              onNavigate={handleNavigate}
              friendRequestCount={incomingCount}
              cachedFriendships={friendships}
            />
          )}
          {screen === "dish-reviews" && dishInfo && (
            <DishReviewsScreen
              visits={visits} friendVisits={friendVisits} dishInfo={dishInfo}
              user={user}
              onBack={() => setScreen("restaurant-detail")}
              onNavigate={handleNavigate}
              friendRequestCount={incomingCount}
            />
          )}
      {screen === "friends" && !showFriendSettings && (
        <FriendsScreen
          user={user}
          onViewFriend={openFriendVisits}
          onNavigate={handleNavigate}
          friendRequestCount={incomingCount}
          cachedFriendships={friendships}
          onOpenFriendSettings={(email) => { setFriendSettingsPrefill(typeof email === "string" ? email : ""); setShowFriendSettings(true); }}
        />
      )}
      {screen === "friends" && showFriendSettings && (
        <FriendSettingsScreen
          user={user}
          onBack={() => setShowFriendSettings(false)}
          onFriendsChanged={() => { setIncomingCount(0); }}
          prefillEmail={friendSettingsPrefill}
          onViewFriend={openFriendVisits}
          onNavigate={handleNavigate}
          friendRequestCount={incomingCount}
        />
      )}
      {screen === "friend-visits" && friendId && (
        <FriendVisitsScreen
          friendId={friendId}
          friendEmail={friendEmail}
          onBack={() => setScreen("friends")}
          onNavigate={handleNavigate}
          friendRequestCount={incomingCount}
        />
      )}
      {screen === "map" && (
        <MapScreen
          user={user}
          onNavigate={handleNavigate}
          friendRequestCount={incomingCount}
          cachedFriendships={friendships}
        />
      )}
        </>
      ) : null}
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────
const styles = {
  appShell: {
    minHeight: "100vh",
    background: "var(--color-background-tertiary)",
  },
  screen: {
    width: "100%",
    maxWidth: 480,
    height: "100dvh",
    background: "var(--color-background-secondary)",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
  },
  topbar: {
    paddingTop: "max(env(safe-area-inset-top), 16px)",
    paddingBottom: 12,
    paddingLeft: 20,
    paddingRight: 20,
    display: "flex",
    flexDirection: "column",
    background: "var(--color-background-primary)",
    borderBottom: "0.5px solid var(--color-border-tertiary)",
    flexShrink: 0,
    zIndex: 10,
  },
  logo: {
    fontFamily: "'Georgia', serif",
    fontSize: 22,
    fontWeight: 600,
    color: "#C94F2C",
    letterSpacing: "-0.3px",
    whiteSpace: "nowrap",
  },
  topbarTitle: {
    fontFamily: "'Georgia', serif",
    fontSize: 18,
    fontWeight: 500,
    color: "var(--color-text-primary)",
  },
  btnPrimary: {
    background: "#C94F2C",
    color: "#fff",
    border: "none",
    borderRadius: 20,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  pillBtn: {
    background: "#C94F2C",
    color: "#fff",
    border: "none",
    borderRadius: 20,
    padding: "7px 16px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  pillBtnGhost: {
    background: "transparent",
    color: "var(--color-text-secondary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: 20,
    padding: "7px 16px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  backBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 15,
    color: "var(--color-text-secondary)",
    fontFamily: "inherit",
    padding: 0,
    width: "auto",
    maxWidth: 160,
    textAlign: "left",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  scrollBody: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 16px 90px",
    WebkitOverflowScrolling: "touch",
  },
  emptyState: {
    textAlign: "center",
    padding: "80px 24px",
  },
  emptyH: {
    fontFamily: "'Georgia', serif",
    fontSize: 20,
    fontWeight: 500,
    color: "var(--color-text-primary)",
    marginBottom: 8,
  },
  emptyP: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    lineHeight: 1.6,
  },
  card: {
    background: "var(--color-background-primary)",
    border: "1px solid var(--color-border-secondary)",
    borderRadius: 14,
    marginBottom: 16,
    cursor: "pointer",
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  cardHeader: {
    padding: "14px 14px 10px",
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
  initial: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: "#FAECE7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Georgia', serif",
    fontSize: 20,
    fontWeight: 600,
    color: "#993C1D",
    flexShrink: 0,
  },
  restName: {
    fontSize: 15,
    fontWeight: 500,
    color: "var(--color-text-primary)",
    marginBottom: 2,
  },
  cardSub: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
  },
  chips: {
    padding: "0 14px 12px",
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
  },
  chip: {
    fontSize: 11,
    padding: "3px 9px",
    borderRadius: 20,
    background: "var(--color-background-secondary)",
    color: "var(--color-text-secondary)",
    border: "0.5px solid var(--color-border-tertiary)",
  },
  bottomNav: {
    background: "var(--color-background-primary)",
    borderTop: "1px solid var(--color-border-secondary)",
    display: "flex",
    alignItems: "stretch",
    flexShrink: 0,
    zIndex: 10,
    paddingBottom: "env(safe-area-inset-bottom)",
  },
  navItem: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    padding: "8px 4px 10px",
    cursor: "pointer",
    overflow: "hidden",
  },
  formSection: {
    marginBottom: 18,
  },
  formLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    marginBottom: 7,
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: 10,
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "inherit",
    fontSize: 15,
    outline: "none",
    boxSizing: "border-box",
  },
  divider: {
    height: "0.5px",
    background: "var(--color-border-tertiary)",
    margin: "18px 0",
  },
  selectedRestaurant: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    background: "var(--color-background-primary)",
    border: "1px solid #C94F2C",
    borderRadius: 10,
  },
  changeBtn: {
    background: "none",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: 20,
    padding: "5px 12px",
    fontSize: 12,
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    fontFamily: "inherit",
    flexShrink: 0,
  },
  actionBtnBlue: {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: 20,
    padding: "7px 16px",
    fontSize: 13,
    fontWeight: 500,
    color: "#C94F2C",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  actionBtnRed: {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: 20,
    padding: "7px 16px",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  manualBox: {
    marginTop: 10,
    padding: 14,
    background: "var(--color-background-secondary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: 10,
  },
  loggedDish: {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-tertiary)",
    borderRadius: 10,
    padding: "10px 14px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  catBadge: {
    fontSize: 10,
    padding: "2px 8px",
    borderRadius: 10,
    background: "var(--color-background-secondary)",
    border: "0.5px solid var(--color-border-tertiary)",
    color: "var(--color-text-tertiary)",
  },
  removeBtn: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "var(--color-background-secondary)",
    border: "0.5px solid var(--color-border-secondary)",
    color: "var(--color-text-tertiary)",
    fontSize: 16,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  dishBuilder: {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: 14,
    padding: 16,
    marginBottom: 14,
  },
  dbField: {
    marginBottom: 16,
  },
  dbLabel: {
    fontSize: 11,
    color: "var(--color-text-tertiary)",
    marginBottom: 7,
    fontWeight: 500,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  photoPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "8px 14px",
    borderRadius: 20,
    border: "0.5px solid var(--color-border-secondary)",
    background: "var(--color-background-secondary)",
    fontSize: 13,
    color: "var(--color-text-secondary)",
    cursor: "pointer",
  },
  addDishBtn: {
    width: "100%",
    padding: 12,
    background: "#C94F2C",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    marginTop: 8,
  },
  saveVisitBtn: {
    width: "100%",
    padding: 14,
    background: "#C94F2C",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontFamily: "inherit",
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
  },
  detailHeader: {
    background: "var(--color-background-primary)",
    borderRadius: 14,
    padding: 16,
    marginBottom: 14,
    border: "0.5px solid var(--color-border-tertiary)",
  },
  detailRest: {
    fontFamily: "'Georgia', serif",
    fontSize: 22,
    fontWeight: 600,
    color: "var(--color-text-primary)",
    marginBottom: 3,
  },
  catGroupLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  catDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#C94F2C",
    display: "inline-block",
    flexShrink: 0,
  },
  detailDish: {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-tertiary)",
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 8,
    overflow: "hidden",
  },
};
